/**
 * Tackle My Day tab.
 *
 * LERT-only in v1 (TACKLE-MY-DAY-FINAL.md §1.2). Non-LERT Photon users see a
 * graceful empty state. Admins may impersonate any LERT via the picker; when
 * active, a "Viewing as" banner makes the impersonation visible at a glance
 * and every backend call carries the same override.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Field,
  Button,
  Badge,
  Textarea,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@fluentui/react-components";
import {
  ChevronDown16Regular,
  ChevronRight16Regular,
  Open16Regular,
  Sparkle16Regular,
  Mail16Regular,
  Call16Regular,
  CheckmarkCircle16Regular,
  Clock16Regular,
  CalendarLtr16Regular,
} from "@fluentui/react-icons";
import { TmdBriefingProgress } from "./TmdBriefingProgress";
import { useOutlookDraft } from "../../hooks/useOutlookDraft";
import { getConfig } from "../../config";
import { EventConversation } from "./EventConversation";
import TeamCockpitPanel, {
  TeamCockpit,
  DrillState,
  DrillSpec,
  drillKey,
  TeamBriefingRec,
  CheckinRepGroup,
  CheckinContactInfo,
} from "./TeamCockpitPanel";

const GHL_BASE = "https://crm.coto.services/v2/location/OZ0gNsIxzemC6UfTxrH2";


interface CrmApi {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  isConfigured: () => boolean;
}

interface TmdProfile {
  id: string;
  display_name: string;
  bucket_strategy:
    | "lert"
    | "pipeline_wide"
    | "pm_by_state"
    | "lert_rollup"
    | "team_lert_aggregate";
}

interface TmdMe {
  is_lert: boolean;
  lert_name: string | null;
  territories: string[];
  is_admin: boolean;
  is_impersonating: boolean;
  actor_name: string | null;
  // Portal.Admin OR crm_assistant_admin — gates the "View as ..." picker.
  can_impersonate_lert?: boolean;
  // Profile-aware fields (Phase 3 backend addition). When null, the user
  // has no TMD profile; when set, drives the panel's render mode (LERT vs
  // pipeline-wide). Frontend continues honoring the legacy is_lert flag
  // as a fallback during the migration.
  profile?: TmdProfile | null;
  // Display name of the impersonation target (set for both LERT and
  // non-LERT impersonation). Banner renders uniformly off this rather
  // than off lert_name, which is null when impersonating Tatiana.
  target_name?: string | null;
}

interface LertMember {
  name: string;
  ghl_user_id: string;
  territories: string[];
}

interface ProfileMember {
  user_id: string;
  /** Set on synthesized LERT entries (sourced from lert_territories.json) —
   * signals the frontend to impersonate via ?impersonate_lert=<name>. */
  lert_name?: string;
  email: string;
  display_name: string;
  profile: { id: string; display_name: string };
}

// Union shape for the merged "View as ..." picker. Each entry is tagged
// with the impersonation mechanism so the frontend knows which query
// param to send (impersonate_lert vs impersonate_user_id).
type PickerMember =
  | {
      mechanism: "lert";
      // LERT name (the value we send back to the backend as
      // ?impersonate_lert=...). Doubles as the display label since LERT
      // entries don't carry a profile label today.
      value: string;
      label: string;
    }
  | {
      mechanism: "user_id";
      // Cosmos user_id (sent as ?impersonate_user_id=...).
      value: string;
      // "Display Name — Profile Display Name"
      label: string;
    };

type Bucket = "owned" | "territory_unassigned" | "territory_other_owned";
type Confidence = "HIGH" | "MEDIUM" | "LOW";

interface NoteItem {
  body: string;
  date: string;
  user: string;
  user_id?: string;
  is_photon: boolean;
}

interface Proposal {
  id: string;
  type: string;
  target?: "custom_field" | "opp_field";
  value_type?: "date" | "number" | "string";
  opp_id: string;
  field_id: string;
  field_name: string;
  current_value: string;
  proposed_value: string;
  proposed_value_display?: string;
  headline: string;
  reason: string;
  source_summary: string;
}

interface Contradiction {
  id: string;
  check: string;
  severity: "info" | "warning" | "critical";
  summary: string;
  details: string;
  opp_id: string;
}

interface Recommendation {
  // Kind discriminator — "opportunity" (default), "task", or "event"
  // (Conference Coordinator v2 — event obligations woven into the brief).
  // Backend backfills "opportunity" when missing so older briefings keep rendering.
  kind?: "opportunity" | "task" | "event";
  // Shared
  confidence?: Confidence;
  confidence_reason?: string;
  headline: string;
  reasoning: string;
  // Event-kind fields (events_agenda.py). card_type is one of
  // attendee | gap | escalation | owner; priority drives the chip.
  card_type?: "attendee" | "gap" | "escalation" | "owner";
  priority?: "high" | "medium" | "low";
  card_id?: string;
  event_id?: string;
  event_name?: string;
  person_id?: string;
  person_name?: string;
  person_email?: string;
  days_remaining?: number;
  overdue?: boolean;
  event_notes?: Array<{ ts?: string; author?: string; body?: string; role?: string }>;
  // Persistent confirm chips carried on the card (facts Photon extracted from
  // the notes but not yet confirmed) — survive reloads, unlike the ephemeral
  // ones from the last add-note response.
  proposed_facts?: Array<{ field: string; value: string; display?: string; confirm_question?: string }>;
  // Opportunity-kind fields
  opp_id?: string;
  opp_name?: string;
  contact?: string;
  pipeline?: string;
  stage?: string;
  bucket?: Bucket;
  notes?: NoteItem[];
  proposals?: Proposal[];
  contradictions?: Contradiction[];
  // PM-side deterministic suggestions (stall / rename / update_fields).
  // Rendered inside the recommendation card with Approve/Dismiss when
  // applicable; mirrors the per-opp blocks the Phase 1 flat-list view used.
  photon_suggests?: PmPhotonSuggest[];
  // PM days-in-stage carried forward from the source opp — used for the
  // dwell badge in the meta row.
  days_in_stage?: number;
  // Non-LERT profile fields (e.g. existing_customer briefing). Populated by
  // briefing-existing-customer.md to drive the email drafter's recipient
  // resolution and tone modifier. Absent for LERT recommendations.
  sub_state?: string;
  recommended_nudge_target?: string;
  recommended_tone?: "standard" | "soft";
  // sales_admin_lert (Allison) rollup fields — the owning rep this deal
  // belongs to (or "Unassigned") and why the scorer surfaced it. Rendered as
  // an owner tag on the card; absent for other profiles.
  owner_name?: string;
  owner_ghl_user_id?: string;
  urgency_reasons?: string[];
  // Optional check-and-decide checklist surfaced when the right action
  // depends on something Photon can't determine from the available
  // signals (e.g. signed vs unsigned proposal). Empty / absent when the
  // action is unambiguous.
  decision_tree?: Array<{ check: string; if_yes?: string; if_no?: string }>;
  // Task-kind fields (backfilled server-side from GHL when Gemini omits them)
  task_id?: string;
  title?: string;
  body?: string;
  due_date?: string;
  days_until_due?: number;
  is_overdue?: boolean;
  contact_id?: string;
  contact_name?: string;
}

interface AmandaIntake {
  fields: Array<{ label: string; value: string }>;
  callNotes: string;
}

// Amanda-style intake parser — tolerant of asterisk- OR newline-delimited
// "Label: value" chunks, always captures the free-text Call Notes block
// spanning to end-of-body. Heuristic: must contain Account Name: or
// Call Notes:, plus at least three labeled fields, so plain notes with
// one colon don't get mis-parsed.
function parseAmandaIntake(body: string): AmandaIntake | null {
  if (!body) return null;
  if (!/(Account Name\s*:|Call Notes\s*:)/i.test(body)) return null;
  const labelMatches = body.match(/(?:^|[\n*])\s*[A-Za-z][A-Za-z0-9 /#-]*?\s*:/gm) || [];
  if (labelMatches.length < 3) return null;

  let callNotes = "";
  let pre = body;
  const cn = body.match(/Call Notes\s*:\s*([\s\S]*)/i);
  if (cn && cn.index !== undefined) {
    callNotes = cn[1].trim();
    pre = body.slice(0, cn.index);
  }

  const asterisks = (pre.match(/\*/g) || []).length;
  const rawChunks = asterisks >= 2 ? pre.split("*") : pre.split(/\r?\n/);
  const chunks = rawChunks.map((c) => c.trim()).filter(Boolean);

  const fields: Array<{ label: string; value: string }> = [];
  for (const chunk of chunks) {
    const m = chunk.match(/^([^:]+):\s*([\s\S]*)$/);
    if (!m) continue;
    const label = m[1].trim();
    const value = m[2].trim();
    if (!value) continue;
    if (/^call notes$/i.test(label)) {
      if (!callNotes) callNotes = value;
    } else {
      fields.push({ label, value });
    }
  }
  if (fields.length === 0 && !callNotes) return null;
  return { fields, callNotes };
}

// Close-confidence card (CLOSE-CONFIDENCE-SPEC.md / PHOTON-74). Populated
// only for profiles with confidence_checks configured (existing_customer
// pilot); absent/empty everywhere else.
interface ConfidenceCheck {
  opp_id: string;
  opp_name: string;
  stage: string;
  pipeline?: string;
  expected_close: string;
  question: string;
  last_answer?: {
    level: Confidence;
    answered_at: string;
    expected_close_at_answer: string;
  } | null;
}

interface BriefingResponse {
  is_lert: boolean;
  message?: string;
  lert_name?: string | null;
  territories?: string[];
  is_impersonating?: boolean;
  actor_name?: string | null;
  generated_at?: string;
  // counts shape varies by bucket strategy. LERT briefings populate
  // owned / territory_unassigned / territory_other_owned. Pipeline-wide
  // briefings (e.g. existing_customer profile) populate `opps`. Both
  // include tasks counts (zero for pipeline-wide v1, real counts for LERT).
  counts?: {
    owned?: number;
    territory_unassigned?: number;
    territory_other_owned?: number;
    opps?: number;
    tasks_in_window?: number;
    tasks_overdue?: number;
    // sales_admin_lert rollup counts.
    candidates?: number;
    reps?: number;
    unassigned?: number;
  };
  // Profile-aware fields populated by build_briefing_for_profile.
  profile_id?: string;
  profile_display_name?: string;
  top_priority?: Recommendation | null;
  recommendations?: Recommendation[];
  notes?: string | null;
  confidence_checks?: ConfidenceCheck[];
  // PM profile (build_briefing_for_pm) — Phase 1: flat list of ops opps
  // routed to this PM by contact state. No AI ranking yet.
  pm_id?: string | null;
  pm_display_name?: string | null;
  opps?: PmOpp[];
  total?: number;
  pipeline_total?: number;
  unrouted_count?: number;
}

interface PmPhotonSuggestUpdate {
  field_id: string;
  field_name: string;
  current: number | null;
  suggested: number;
}

interface PmPhotonSuggest {
  /** Stable hash for server-side regen check on Approve. */
  id?: string;
  kind: "rename" | "update_fields" | "stall";
  headline: string;
  reason: string;
  /** Whether the suggestion has a concrete Approve action; false when
   *  the parser couldn't propose a specific value (rename block falls
   *  back to showing the template only). */
  approvable?: boolean;
  /** Concrete proposed new opp name (for kind="rename"). */
  suggested_name?: string;
  /** Original opp name at suggestion time. */
  current_name?: string;
  /** Template hint shown when no concrete name could be inferred. */
  suggested_template?: string;
  updates?: PmPhotonSuggestUpdate[];
  owner?: "client" | "pm" | "team" | "warehouse" | "finance" | "unknown";
  business_days?: number;
  stage_number?: number;
}

interface PmOpp {
  id: string;
  name: string;
  stage_id: string;
  stage_name: string;
  days_in_stage: number;
  contact_id?: string;
  contact_name?: string;
  company_name?: string;
  state?: string | null;
  state_source?: "contact" | "name" | "unknown";
  monetary_value?: number | null;
  assigned_to?: string | null;
  status?: string;
  last_stage_change_at?: string | null;
  photon_suggests?: PmPhotonSuggest[];
  notes?: NoteItem[];
}

const BUCKET_LABEL: Record<Bucket, string> = {
  owned: "Owned",
  territory_unassigned: "Territory — unassigned",
  territory_other_owned: "Territory — other LERT",
};

const BUCKET_COLOR: Record<Bucket, "success" | "warning" | "informative"> = {
  owned: "success",
  territory_unassigned: "warning",
  territory_other_owned: "informative",
};

const CONFIDENCE_LABEL: Record<Confidence, string> = {
  HIGH: "HIGH",
  MEDIUM: "MED",
  LOW: "LOW",
};

const useStyles = makeStyles({
  container: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
    overflowY: "auto",
  },
  controls: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  nativeSelect: {
    padding: "6px 12px",
    borderRadius: "4px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    fontSize: "14px",
    width: "250px",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
  },
  emptyState: {
    maxWidth: "600px",
    margin: "0 auto",
    padding: tokens.spacingVerticalXXL,
    textAlign: "center",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  metaRow: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
    alignItems: "center",
    color: tokens.colorNeutralForeground3,
    fontSize: "13px",
    flexWrap: "wrap",
  },
  topPriorityCard: {
    padding: tokens.spacingVerticalL,
    borderLeft: `4px solid ${tokens.colorPaletteGreenBorder2}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  topPriorityLabel: {
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  topPriorityHeadline: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: tokens.spacingVerticalS,
    lineHeight: 1.3,
  },
  recCard: {
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  recHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalM,
  },
  recHeadline: {
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: 1.35,
    marginBottom: tokens.spacingVerticalXS,
  },
  recSubline: {
    color: tokens.colorNeutralForeground3,
    fontSize: "13px",
  },
  reasoningText: {
    marginTop: tokens.spacingVerticalS,
    fontSize: "14px",
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.45,
  },
  notes: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  expandToggle: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: tokens.colorNeutralForeground2,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "13px",
    marginTop: tokens.spacingVerticalS,
  },
  notesBlock: {
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  intakeCard: {
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  intakeTitle: {
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalS,
  },
  intakeFieldGrid: {
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalXS,
    fontSize: "13px",
    marginBottom: tokens.spacingVerticalS,
  },
  intakeLabel: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  intakeCallNotes: {
    marginTop: tokens.spacingVerticalS,
    whiteSpace: "pre-wrap",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  noteItem: {
    fontSize: "13px",
  },
  noteMeta: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    marginBottom: tokens.spacingVerticalXXS,
  },
  noteBody: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.45,
  },
  photonBlock: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: tokens.spacingVerticalS,
  },
  photonToggle: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: tokens.colorNeutralForeground2,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  openInGhl: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: tokens.colorBrandForegroundLink,
    textDecoration: "none",
    marginTop: tokens.spacingVerticalXS,
  },
  askPhoton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: tokens.colorBrandForegroundLink,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    marginTop: tokens.spacingVerticalXS,
    fontFamily: "inherit",
  },
  notesActions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  addNoteBlock: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  addNoteActions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
  addNoteError: {
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
  addNoteToggle: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: tokens.colorBrandForegroundLink,
    fontSize: "12px",
    fontFamily: "inherit",
  },
  emailLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: tokens.colorBrandForegroundLink,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  // Sales-Rep voice visually distinct from Photon's voice — solid green
  // border + off-white background, labeled "SALES REP DRAFT (YOUR VOICE)".
  // Keeps the spec §9.3 handoff clear: Photon introduces, Sales Rep speaks.
  draftContainer: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    border: `2px solid ${tokens.colorPaletteGreenBorder2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  draftLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorPaletteGreenForeground2,
  },
  draftFieldRow: {
    display: "grid",
    gridTemplateColumns: "60px 1fr",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    fontSize: "13px",
  },
  draftFieldLabel: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  draftSmallInput: {
    padding: "4px 8px",
    borderRadius: "4px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: "13px",
    fontFamily: "inherit",
    width: "100%",
  },
  ccPicker: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalXXS,
  },
  ccLocked: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    padding: "2px 8px",
  },
  draftReasoning: {
    fontSize: "12px",
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
  },
  draftActions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    marginTop: tokens.spacingVerticalXS,
  },
  draftError: {
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
  draftDeclined: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    padding: tokens.spacingVerticalS,
    borderLeft: `3px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  draftSuccess: {
    fontSize: "13px",
    color: tokens.colorPaletteGreenForeground2,
    fontStyle: "italic",
  },
  // Talk-track shares the Sales-Rep visual treatment but uses a blue-ish
  // border so the two Sales-Rep artifacts (email vs call prep) are easy to
  // tell apart at a glance when both are open on the same card.
  trackContainer: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    border: `2px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  trackLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorBrandForeground1,
  },
  trackCallType: {
    fontSize: "12px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  trackSection: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  trackSectionHeader: {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    color: tokens.colorNeutralForeground3,
  },
  trackText: {
    fontSize: "13px",
    lineHeight: 1.5,
  },
  trackList: {
    margin: 0,
    paddingLeft: "18px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  confidenceBadge: {
    display: "inline-block",
    padding: "2px 6px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    borderRadius: "3px",
    marginLeft: tokens.spacingHorizontalS,
    verticalAlign: "middle",
    lineHeight: 1.2,
  },
  confidenceHigh: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
  },
  confidenceMedium: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  confidenceLow: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    fontStyle: "italic",
  },
  confidenceReason: {
    display: "block",
    marginTop: tokens.spacingVerticalXXS,
    fontSize: "12px",
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
  },
  // Close-confidence check-in cards (PHOTON-74)
  ccCard: {
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  ccSubline: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground2,
  },
  ccLastAnswer: {
    fontSize: "12px",
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
  },
  ccWhy: {
    width: "100%",
  },
  flagBlock: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorPaletteYellowBorder2}`,
    borderLeft: `3px solid ${tokens.colorPaletteYellowBorder2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  flagLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorPaletteYellowForeground2,
  },
  flagSummary: {
    fontSize: "14px",
    fontWeight: 600,
  },
  flagDetails: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.4,
  },
  flagActions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    marginTop: tokens.spacingVerticalXS,
  },
  flagError: {
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
  flagDismissed: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  proposalBlock: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    borderLeft: `3px solid ${tokens.colorPaletteGreenBorder2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  proposalLabel: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground2,
  },
  proposalHeadline: {
    fontSize: "14px",
    fontWeight: 600,
  },
  proposalReason: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.4,
  },
  proposalActions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    marginTop: tokens.spacingVerticalXS,
  },
  proposalError: {
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
  proposalSuccess: {
    fontSize: "12px",
    color: tokens.colorPaletteGreenForeground2,
    fontStyle: "italic",
  },
  askPhotonIcon: {
    color: "#4CAF50",
  },
  noNotes: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  // Task card — visually distinct from opp card via a brand-blue left border
  // (opp recs have no colored border). The blue evokes the "scheduled
  // commitment" feel vs. the pipeline opps on either side.
  taskCardLeftBorder: {
    borderLeft: `3px solid ${tokens.colorBrandStroke2}`,
  },
  taskDueBadge: {
    display: "inline-block",
    fontSize: "11px",
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "3px",
    letterSpacing: "0.3px",
    verticalAlign: "middle",
  },
  taskDueOverdue: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
  },
  taskDueToday: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
    border: `1px solid ${tokens.colorPaletteYellowBorder2}`,
  },
  taskDueSoon: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  taskBody: {
    marginTop: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  taskActions: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "center",
    marginTop: tokens.spacingVerticalM,
    flexWrap: "wrap",
  },
  taskStatus: {
    fontSize: "13px",
    color: tokens.colorPaletteGreenForeground2,
    fontStyle: "italic",
  },
  taskError: {
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
});

interface TackleMyDayPanelProps {
  crmApi: CrmApi;
  /** Called when the user clicks Ask Photon on a rec; parent switches to AI Assistant with the prompt. */
  onAskAboutOpp?: (prompt: string, title?: string) => void;
}

function buildAskTitle(rec: Recommendation): string {
  const bits: string[] = [];
  if (rec.opp_name) bits.push(rec.opp_name);
  if (rec.contact) bits.push(rec.contact);
  return bits.join(" · ").slice(0, 200);
}

function buildTaskAskTitle(rec: Recommendation): string {
  const bits = ["Task"];
  if (rec.contact_name) bits.push(rec.contact_name);
  if (rec.title) bits.push(rec.title);
  return bits.join(" · ").slice(0, 200);
}

function buildTaskAskPrompt(rec: Recommendation): string {
  const parts: string[] = [];
  parts.push(
    "ABSOLUTE READ-ONLY REQUEST. This is a request for information only. You must not call any write endpoints on the CRM API. Only GET requests are permitted. Forbidden operations include: PUT /opportunities/*, POST /opportunities/*, POST /opportunities/*/tasks, PUT /opportunities/*/tasks/*, DELETE /opportunities/*/tasks/*, POST /contacts/*/tasks, PUT /contacts/*, POST /contacts/*/notes, PUT /contacts/*/notes/*, DELETE /contacts/*/notes/*, POST /conversations/messages, POST /tasks, PUT /tasks/*, DELETE /tasks/*, or any endpoint that creates, updates, deletes, moves, assigns, sends, or schedules data.",
  );
  const dueStr = rec.due_date ? new Date(rec.due_date).toLocaleDateString() : "";
  parts.push(
    `I have an open task "${rec.title || ""}" for ${rec.contact_name || "a contact"}${dueStr ? ` (due ${dueStr})` : ""}.`,
  );
  if (rec.reasoning) parts.push(`Tackle My Day flagged it because: ${rec.reasoning}`);
  parts.push(
    "Please prep me for this task with a written report covering (1) who this contact is and their role, (2) recent activity (notes, messages, opps, campaigns), (3) any open opportunities or recent changes, and (4) what you would suggest discussing or confirming — described in words only.",
  );
  parts.push(
    "To be absolutely clear: I want a written report, not actions. If you are about to call a tool that would write, update, move, delete, or send anything, stop and instead describe what you would have done.",
  );
  return parts.join(" ");
}

function formatDueLabel(days: number | undefined, isOverdue: boolean | undefined): string {
  if (days === undefined || days === null) return "No due date";
  if (isOverdue) {
    const d = Math.abs(days);
    return d <= 1 ? "Overdue" : `Overdue ${d}d`;
  }
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days}d`;
}

function buildAskPrompt(rec: Recommendation): string {
  const parts: string[] = [];
  parts.push(
    "ABSOLUTE READ-ONLY REQUEST. This is a request for information only. You must not call any write endpoints on the CRM API. Only GET requests are permitted. Forbidden operations include: PUT /opportunities/*, POST /opportunities/*, POST /opportunities/*/tasks, PUT /opportunities/*/tasks/*, DELETE /opportunities/*/tasks/*, POST /contacts/*/tasks, PUT /contacts/*, POST /contacts/*/notes, PUT /contacts/*/notes/*, DELETE /contacts/*/notes/*, POST /conversations/messages, POST /tasks, PUT /tasks/*, DELETE /tasks/*, or any endpoint that creates, updates, deletes, moves, assigns, sends, or schedules data. Do not move the opportunity between pipelines or stages. Do not change the assigned owner. Do not update custom fields. Do not create, complete, or delete tasks. Do not send emails or messages. Do not write or edit notes.",
  );
  parts.push(
    `I'm looking at the ${rec.opp_name} opportunity${rec.contact ? ` with ${rec.contact}` : ""}${rec.pipeline ? ` in the ${rec.pipeline} pipeline` : ""}${rec.stage ? ` (currently ${rec.stage})` : ""}.`,
  );
  if (rec.reasoning) parts.push(`Tackle My Day flagged it because: ${rec.reasoning}`);
  parts.push(
    "Please give me a written report covering (1) recent contact notes and what's in them, (2) the stakeholders involved, (3) any risks, competitors, or open questions, and (4) what you would suggest as a next move — described in words only.",
  );
  parts.push(
    "To be absolutely clear: I want a written report, not actions. If you are about to call a tool that would write, update, move, delete, or send anything, stop and instead describe what you would have done. Every sentence in your response must be something you are telling me — not something you have done or are about to do.",
  );
  return parts.join(" ");
}

// API errors arrive as "HTTP 429: {json}". Show the server's human sentence
// (detail.reason, or a string detail) instead of the raw payload.
function friendlyApiError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  const m = /^HTTP \d+:\s*([\s\S]*)$/.exec(msg);
  if (!m) return msg;
  try {
    const body = JSON.parse(m[1]);
    const detail = body?.detail ?? body;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail.reason === "string") return detail.reason;
  } catch {
    /* not JSON — fall through */
  }
  return msg;
}

export const TackleMyDayPanel: React.FC<TackleMyDayPanelProps> = ({ crmApi, onAskAboutOpp }) => {
  const styles = useStyles();
  const [me, setMe] = useState<TmdMe | null>(null);
  // Internal-coordination profiles draft nudges to colleagues, not customer
  // email — they keep their configured To and always send via GHL.
  const isInternalNudgeProfile =
    me?.profile?.bucket_strategy === "pipeline_wide" ||
    me?.profile?.bucket_strategy === "pm_by_state" ||
    me?.profile?.bucket_strategy === "lert_rollup";
  const [pickerMembers, setPickerMembers] = useState<PickerMember[]>([]);
  // Picker selection: encoded as "mechanism:value" so we can round-trip
  // through a single <select> without an object-shape state. Empty string
  // = no impersonation (own identity).
  const [impersonate, setImpersonate] = useState<string>("");
  const [briefing, setBriefing] = useState<BriefingResponse | null>(null);
  // Conference Coordinator v2 — the create/interview conversation, opened from
  // a button in the coordinator's brief.
  const [convoOpen, setConvoOpen] = useState(false);
  // Team-lead cockpit (team_lert_aggregate profile / Greg). Greg is a
  // player-manager, so his panel shows BOTH his personal briefing above and
  // the team cockpit — a [My Day | Team Cockpit] toggle switches between them.
  // teamView defaults to "cockpit" (his leadership landing view).
  const [teamCockpit, setTeamCockpit] = useState<TeamCockpit | null>(null);
  // Team TMD (coach) — the cross-rep AI-ranked list shown in the cockpit's right
  // column. Reuses the standard BriefingResponse shape (rollup path). Loaded
  // independently of the cockpit so its slower Gemini call doesn't block the rail.
  const [teamBriefing, setTeamBriefing] = useState<BriefingResponse | null>(null);
  const [teamBriefingLoading, setTeamBriefingLoading] = useState(false);
  const [teamView, setTeamView] = useState<"myday" | "cockpit">("cockpit");
  // Rail drill-down: the opps behind a clicked rail tile (close window, deals
  // closed, new opps, pilots). null = show the coach list; set = the right
  // column lists that tile's opps.
  const [drill, setDrill] = useState<DrillState | null>(null);
  // "Show more" appends additional recs without disturbing the originals
  // (re-ranking mid-session shuffles cards the user was about to act on —
  // same reason proposalState doesn't auto-refresh). Each click of Show More
  // calls /briefing with an exclude list of every opp_id already on screen
  // and appends the new top_priority + recommendations here. Reset on every
  // loadAll so a manual Refresh starts the rotation over.
  const [extraRecs, setExtraRecs] = useState<Recommendation[]>([]);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [noMoreRecs, setNoMoreRecs] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [photonExpanded, setPhotonExpanded] = useState<Set<string>>(new Set());
  // proposalState[proposalId] tracks in-flight and settled state per button.
  // "pending" = a request is in-flight (both buttons disabled). "approved" /
  // "dismissed" = terminal; proposal renders in a collapsed success state
  // and stays in place until the user clicks Refresh. We deliberately do NOT
  // re-fetch the briefing on each action — re-ranking mid-task shuffled cards
  // out from under the user (a sibling proposal they were about to click
  // moved or fell off the visible list).
  const [proposalState, setProposalState] = useState<Record<string, { status: "pending" | "approved" | "dismissed"; error?: string }>>({});
  // flagState[flag_id] mirrors proposalState for contradiction dismissals.
  const [flagState, setFlagState] = useState<Record<string, { status: "pending" | "dismissed"; error?: string }>>({});
  // taskState[task_id]: per-task in-flight + settled state for Mark Complete
  // and Snooze. Settled entries stay visible (in their settled UI state) until
  // the next manual refresh — see the proposalState comment above for the
  // rationale.
  const [taskState, setTaskState] = useState<Record<string, { status: "idle" | "completing" | "completed" | "snoozing" | "snoozed" | "error"; error?: string }>>({});
  // noteState[opp_id]: composer open/closed + in-flight + error
  const [noteState, setNoteState] = useState<Record<string, { open: boolean; text: string; busy: boolean; error?: string }>>({});
  // eventCardState[card_id]: Conference Coordinator event-card UI — notes &
  // context expander + add-a-note (which runs an interview turn) + a drafted
  // nudge email (escalation cards).
  const [eventCardState, setEventCardState] = useState<Record<string, {
    notesOpen?: boolean;
    noteText?: string;
    noteBusy?: boolean;
    noteMsg?: string;
    // Facts Photon extracted from the last note, offered as one-tap confirm
    // chips — confirming commits the value (e.g. show dates) so a gap resolves.
    proposedFacts?: Array<{ field: string; value: string; display?: string; confirm_question?: string }>;
    confirmingField?: string;
    draft?: { draft_available?: boolean; reason?: string; subject?: string; body_plain?: string; body_html?: string; to_name?: string; to_email?: string } | null;
    draftBusy?: boolean;
    sending?: boolean;
    sent?: boolean;
    err?: string;
  }>>({});
  // emailState[opp_id]: open | drafting | draft available (editable) | sending | sent | declined | error
  const [emailState, setEmailState] = useState<Record<string, {
    status: "drafting" | "draft" | "sending" | "sent" | "drafted" | "declined" | "recency" | "error";
    to_email?: string;
    to_name?: string;
    from_email?: string;
    // cc_emails: CC(s) the draft itself carried (e.g. a rollup/nudge draft's
    // configured CC) — rendered locked so a configured CC can't be dropped.
    cc_emails?: string[];
    // Customer emails (PHOTON-118): free-typed To / Cc / Bcc lines,
    // comma-separated. Internal nudges keep the single editable to_email.
    customer?: boolean;
    to_line?: string;
    cc_line?: string;
    bcc_line?: string;
    // Outlook draft (PHOTON-117): link to the draft, and a CRM-logging error
    // if the draft exists but the GHL note didn't land.
    drafted_link?: string;
    drafted_id?: string;
    record_error?: string;
    // The LERT already overrode the 72h gate when the draft was written
    // ("Draft anyway"); carry it to send / Outlook so they aren't asked twice.
    overridden?: boolean;
    subject?: string;
    body_plain?: string;
    body_html?: string;
    reasoning?: string;
    declined_reason?: string;
    can_override?: boolean;
    error?: string;
  }>>({});

  // trackState[opp_id]: loading → available (display) → declined/recency/error
  const [trackState, setTrackState] = useState<Record<string, {
    status: "loading" | "available" | "declined" | "recency" | "error";
    call_type?: string;
    context_summary?: string;
    opener?: string;
    questions?: string[];
    listen_for?: string[];
    close?: string;
    reasoning?: string;
    declined_reason?: string;
    can_override?: boolean;
    error?: string;
  }>>({});

  const setEmailField = useCallback((oppId: string, patch: Partial<{
    status: "drafting" | "draft" | "sending" | "sent" | "drafted" | "declined" | "recency" | "error";
    to_email?: string;
    to_name?: string;
    from_email?: string;
    cc_emails?: string[];
    customer?: boolean;
    to_line?: string;
    cc_line?: string;
    bcc_line?: string;
    drafted_link?: string;
    drafted_id?: string;
    record_error?: string;
    // The LERT already overrode the 72h gate when the draft was written
    // ("Draft anyway"); carry it to send / Outlook so they aren't asked twice.
    overridden?: boolean;
    subject?: string;
    body_plain?: string;
    body_html?: string;
    reasoning?: string;
    declined_reason?: string;
    error?: string;
  }>) => {
    setEmailState((prev) => {
      const existing = prev[oppId];
      if (!existing && patch.status) return { ...prev, [oppId]: { status: patch.status, ...patch } };
      if (!existing) return prev;
      return { ...prev, [oppId]: { ...existing, ...patch } };
    });
  }, []);

  const outlookEnabled = getConfig().enableOutlookDrafts;
  const { createDraft: createOutlookDraft } = useOutlookDraft();

  const setNoteField = useCallback((oppId: string, patch: Partial<{ open: boolean; text: string; busy: boolean; error?: string }>) => {
    setNoteState((prev) => {
      const existing = prev[oppId] ?? { open: false, text: "", busy: false };
      return { ...prev, [oppId]: { ...existing, ...patch } };
    });
  }, []);

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const togglePhotonExpanded = useCallback((key: string) => {
    setPhotonExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Open an opp in GHL in a new tab — used by the team cockpit's priority rows.
  const openOpp = useCallback((oppId: string) => {
    if (!oppId) return;
    window.open(
      `${GHL_BASE}/opportunities/${oppId}?tab=Opportunity+Details`,
      "_blank",
      "noopener",
    );
  }, []);

  // Picker value is "mechanism:rawValue" — split it back out so we send the
  // right query param / body field to the backend.
  const impersonateParam = (() => {
    if (!impersonate) return "";
    const sep = impersonate.indexOf(":");
    if (sep < 0) return "";
    const mechanism = impersonate.slice(0, sep);
    const rawValue = impersonate.slice(sep + 1);
    if (!rawValue) return "";
    if (mechanism === "lert") {
      return `?impersonate_lert=${encodeURIComponent(rawValue)}`;
    }
    if (mechanism === "user_id") {
      return `?impersonate_user_id=${encodeURIComponent(rawValue)}`;
    }
    return "";
  })();

  // Same dispatch as `impersonateParam` but for action-POST body fields.
  // Returns an object spreadable into a request payload — sends
  // `impersonate_lert` for the legacy LERT picker, `impersonate_user_id`
  // for profile-keyed users (Tatiana, etc.). Empty when not impersonating.
  const impersonateBody: Record<string, string> = (() => {
    const out: Record<string, string> = {};
    if (!impersonate) return out;
    const sep = impersonate.indexOf(":");
    if (sep < 0) return out;
    const mechanism = impersonate.slice(0, sep);
    const rawValue = impersonate.slice(sep + 1);
    if (!rawValue) return out;
    if (mechanism === "lert") out.impersonate_lert = rawValue;
    else if (mechanism === "user_id") out.impersonate_user_id = rawValue;
    return out;
  })();

  const loadAll = useCallback(async () => {
    setLoading(true);
    setBriefing(null);
    setExtraRecs([]);
    setNoMoreRecs(false);
    setError(null);
    try {
      const meRes = await crmApi.get<TmdMe>(`/api/tmd/me${impersonateParam}`);
      setMe(meRes);
      if (meRes.can_impersonate_lert && pickerMembers.length === 0) {
        // Prefer the profile-members picker (Cosmos users with tmd_profile
        // set) — that's the long-term source of truth. Fall back to the
        // legacy LERT picker (lert_territories.json source) ONLY when no
        // profile entries exist, which happens in environments where the
        // tmd_profile migration hasn't been run yet. Once profile-members
        // has any entry, we trust it as complete — mixing the two lists
        // duplicates LERTs because the display names differ between
        // sources ("Michael D. Natale" vs "Mike Natale") and label-based
        // dedup can't catch it.
        const profileRes = await crmApi.get<{ members: ProfileMember[] }>(
          "/api/tmd/profile-members",
        );
        const profileEntries: PickerMember[] = (profileRes.members || []).map(
          (m) =>
            m.lert_name
              ? {
                  mechanism: "lert" as const,
                  value: m.lert_name,
                  label: `${m.display_name} — ${m.profile.display_name}`,
                }
              : {
                  mechanism: "user_id" as const,
                  value: m.user_id,
                  label: `${m.display_name} — ${m.profile.display_name}`,
                },
        );
        if (profileEntries.length > 0) {
          setPickerMembers(profileEntries);
        } else {
          const lertRes = await crmApi.get<{ members: LertMember[] }>(
            "/api/tmd/lert-members",
          );
          const lertEntries: PickerMember[] = (lertRes.members || []).map((m) => ({
            mechanism: "lert" as const,
            value: m.name,
            label: m.name,
          }));
          setPickerMembers(lertEntries);
        }
      }
      // Team lead (Greg, player-manager): fetch BOTH his personal briefing
      // ("My Day") and the team cockpit so the toggle can switch between them
      // without a reload. The cockpit is team-wide (no impersonation param).
      // Other profiles fetch only their briefing.
      const isTeamLeadView =
        meRes.profile?.bucket_strategy === "team_lert_aggregate";
      const [briefingRes, cockpitRes] = await Promise.all([
        crmApi.get<BriefingResponse>(`/api/tmd/briefing${impersonateParam}`),
        isTeamLeadView
          ? crmApi.get<TeamCockpit>("/api/tmd/team-cockpit")
          : Promise.resolve(null),
      ]);
      setBriefing(briefingRes);
      setTeamCockpit(cockpitRes);
      // A fresh cockpit invalidates any open rail drill-down.
      setDrill(null);
      // Team TMD (cross-rep AI-ranked coach list) is a slower Gemini call — fetch
      // it independently so it never holds back the fast, deterministic metric
      // rail. The cockpit's right column shows a skeleton until it lands, and
      // falls back to the deterministic priority list on failure/empty.
      if (isTeamLeadView) {
        setTeamBriefingLoading(true);
        crmApi
          .get<BriefingResponse>("/api/tmd/team-briefing")
          .then((r) => setTeamBriefing(r))
          .catch(() => setTeamBriefing(null))
          .finally(() => setTeamBriefingLoading(false));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load Tackle My Day");
    } finally {
      setLoading(false);
    }
  }, [crmApi, impersonateParam, pickerMembers.length]);

  const loadMore = useCallback(async () => {
    if (!briefing) return;
    const seenOppIds = new Set<string>();
    const seenTaskIds = new Set<string>();
    const collectFrom = (rec: Recommendation | null | undefined) => {
      if (!rec) return;
      if (rec.opp_id) seenOppIds.add(rec.opp_id);
      if (rec.task_id) seenTaskIds.add(rec.task_id);
    };
    collectFrom(briefing.top_priority);
    (briefing.recommendations || []).forEach(collectFrom);
    extraRecs.forEach(collectFrom);

    setLoadingMore(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (seenOppIds.size > 0) {
        params.set("exclude_opp_ids", Array.from(seenOppIds).join(","));
      }
      if (seenTaskIds.size > 0) {
        params.set("exclude_task_ids", Array.from(seenTaskIds).join(","));
      }
      const sep = impersonateParam ? "&" : "?";
      const path = `/api/tmd/briefing${impersonateParam}${sep}${params.toString()}`;
      const more = await crmApi.get<BriefingResponse>(path);
      const newRecs: Recommendation[] = [];
      if (more.top_priority) newRecs.push(more.top_priority);
      if (more.recommendations) newRecs.push(...more.recommendations);
      if (newRecs.length === 0) {
        setNoMoreRecs(true);
      } else {
        setExtraRecs((prev) => [...prev, ...newRecs]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }, [briefing, extraRecs, crmApi, impersonateParam]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Kept above the early returns below so React always sees the same hook
  // count per render (Rules of Hooks — "Rendered fewer hooks than expected"
  // if declared below a conditional return).
  const saveNote = useCallback(
    async (oppId: string) => {
      const cur = noteState[oppId];
      const text = (cur?.text || "").trim();
      if (!text) return;
      setNoteField(oppId, { busy: true, error: undefined });
      try {
        const payload: Record<string, string> = { opp_id: oppId, body: text };
        Object.assign(payload, impersonateBody);
        await crmApi.post("/api/tmd/actions/add-note", payload);
        setNoteField(oppId, { open: false, text: "", busy: false, error: undefined });
      } catch (e) {
        setNoteField(oppId, {
          busy: false,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [crmApi, impersonate, noteState, setNoteField],
  );

  // Task-kind add-note: writes directly to the rec's contact (no opp).
  // Uses the same noteState map keyed by task_id. Server endpoint accepts
  // either opp_id or contact_id; tasks pass contact_id + task_id (task_id
  // is informational — the note attaches to the contact).
  const saveTaskNote = useCallback(
    async (taskId: string, contactId: string) => {
      const cur = noteState[taskId];
      const text = (cur?.text || "").trim();
      if (!text) return;
      if (!contactId) {
        setNoteField(taskId, { error: "No contact on this task to attach a note to." });
        return;
      }
      setNoteField(taskId, { busy: true, error: undefined });
      try {
        const payload: Record<string, string> = {
          contact_id: contactId,
          task_id: taskId,
          body: text,
        };
        Object.assign(payload, impersonateBody);
        await crmApi.post("/api/tmd/actions/add-note", payload);
        setNoteField(taskId, { open: false, text: "", busy: false, error: undefined });
      } catch (e) {
        setNoteField(taskId, {
          busy: false,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [crmApi, impersonate, noteState, setNoteField],
  );

  const startTalkTrack = useCallback(
    async (oppId: string, force: boolean = false) => {
      setTrackState((prev) => ({ ...prev, [oppId]: { status: "loading" } }));
      try {
        const payload: Record<string, unknown> = { opp_id: oppId };
        Object.assign(payload, impersonateBody);
        if (force) payload.force = true;
        const resp = await crmApi.post<{
          prep_available: boolean;
          reason?: string;
          recency_block?: boolean;
          can_override?: boolean;
          call_type?: string;
          context_summary?: string;
          opener?: string;
          questions?: string[];
          listen_for?: string[];
          close?: string;
          reasoning?: string;
        }>("/api/tmd/actions/talk-track", payload);
        if (!resp.prep_available) {
          setTrackState((prev) => ({
            ...prev,
            [oppId]: {
              status: resp.recency_block ? "recency" : "declined",
              declined_reason: resp.reason || "Generator declined.",
              can_override: Boolean(resp.can_override),
            },
          }));
          return;
        }
        setTrackState((prev) => ({
          ...prev,
          [oppId]: {
            status: "available",
            call_type: resp.call_type,
            context_summary: resp.context_summary,
            opener: resp.opener,
            questions: resp.questions,
            listen_for: resp.listen_for,
            close: resp.close,
            reasoning: resp.reasoning,
          },
        }));
      } catch (e) {
        setTrackState((prev) => ({
          ...prev,
          [oppId]: { status: "error", error: e instanceof Error ? e.message : String(e) },
        }));
      }
    },
    [crmApi, impersonate],
  );

  const closeTalkTrack = useCallback((oppId: string) => {
    setTrackState((prev) => {
      const next = { ...prev };
      delete next[oppId];
      return next;
    });
  }, []);

  const startDraftEmail = useCallback(
    async (
      oppId: string,
      force: boolean = false,
      rec?: Recommendation,
      nudgeTargetOverride?: string,
    ) => {
      setEmailState((prev) => ({ ...prev, [oppId]: { status: "drafting" } }));
      try {
        const payload: Record<string, unknown> = { opp_id: oppId, email_type: "follow_up" };
        Object.assign(payload, impersonateBody);
        if (force) payload.force = true;
        // Non-LERT profile context — pass the rec details so the backend's
        // internal-recipient drafter has the briefing context (nudge target
        // and tone modifier come from the briefing-existing-customer prompt
        // output). Backend ignores these for LERT profiles.
        if (rec) {
          if (rec.recommended_nudge_target) payload.nudge_target = rec.recommended_nudge_target;
          if (rec.recommended_tone) payload.tone_modifier = rec.recommended_tone;
          if (rec.headline) payload.rec_headline = rec.headline;
          if (rec.reasoning) payload.rec_reasoning = rec.reasoning;
          if (rec.sub_state) payload.rec_sub_state = rec.sub_state;
        }
        // Explicit override (e.g. the sales_admin_lert "Finance/PEA nudge"
        // button routes To Schuyler regardless of the briefing's target).
        if (nudgeTargetOverride) payload.nudge_target = nudgeTargetOverride;
        const resp = await crmApi.post<{
          draft_available: boolean;
          reason?: string;
          recency_block?: boolean;
          can_override?: boolean;
          to_email?: string;
          to_name?: string;
          from_email?: string;
          cc_emails?: string[];
          subject?: string;
          body_plain?: string;
          body_html?: string;
          reasoning?: string;
        }>("/api/tmd/actions/email-draft", payload);
        if (!resp.draft_available) {
          setEmailState((prev) => ({
            ...prev,
            [oppId]: {
              status: resp.recency_block ? "recency" : "declined",
              declined_reason: resp.reason || "Drafter declined.",
              can_override: Boolean(resp.can_override),
            },
          }));
          return;
        }
        const draftCc = resp.cc_emails || [];
        // Customer emails get the To/Cc picker; internal nudges (Tatiana,
        // PMs, Allison's rollup) keep their configured To + locked CCs.
        const customer = !isInternalNudgeProfile;
        setEmailState((prev) => ({
          ...prev,
          [oppId]: {
            status: "draft",
            overridden: force,
            to_email: resp.to_email || "",
            to_name: resp.to_name || "",
            from_email: resp.from_email || "",
            cc_emails: draftCc,
            customer,
            to_line: resp.to_email || "",
            cc_line: "",
            bcc_line: "",
            subject: resp.subject || "",
            body_plain: resp.body_plain || "",
            body_html: resp.body_html || "",
            reasoning: resp.reasoning || "",
          },
        }));
      } catch (e) {
        setEmailState((prev) => ({
          ...prev,
          [oppId]: { status: "error", error: e instanceof Error ? e.message : String(e) },
        }));
      }
    },
    [crmApi, impersonate, isInternalNudgeProfile],
  );

  // Team TMD (coach): leave a note on a team deal. It posts authored as the
  // signed-in team lead (Greg) via /actions/team-note. Additive — no reassign.
  const coachNote = useCallback(
    async (oppId: string, text: string) => {
      await crmApi.post("/api/tmd/actions/team-note", { opp_id: oppId, body: text });
    },
    [crmApi]
  );

  // AI-suggested coach note (todo #3): draft a starting point from the deal's
  // status. Returns the suggestion text for the composer to drop in (editable).
  const suggestCoachNote = useCallback(
    async (oppId: string): Promise<string> => {
      const r = await crmApi.post<{ available: boolean; suggestion: string; reason?: string }>(
        "/api/tmd/actions/coach-note-suggest",
        { opp_id: oppId }
      );
      if (!r.available) throw new Error(r.reason || "Couldn't draft a suggestion.");
      return r.suggestion || "";
    },
    [crmApi]
  );

  // Drill into a rail tile: fetch the opps behind it and hand them to the
  // cockpit's right column. Skeleton while it loads; a failed fetch shows an
  // empty list with a message rather than tearing down the drill-down.
  const openDrill = useCallback(
    async (spec: DrillSpec) => {
      // Per-kind: the endpoint to fetch, the header sticker, whether the list is
      // read-only (won / concluded deals), and the empty-state message.
      const meta: {
        url: string;
        sticker: string;
        readOnly: boolean;
        emptyText: string;
        label: string;
      } = (() => {
        switch (spec.kind) {
          case "close-window":
            return {
              url: `/api/tmd/team-close-window?days=${spec.days}`,
              sticker: "📅",
              readOnly: false,
              emptyText: "No deals closing in this window.",
              label: "Close window",
            };
          case "deals-closed":
            return {
              url: `/api/tmd/team-deals-closed?period=${spec.period}`,
              sticker: "🏆",
              readOnly: true,
              emptyText: "No new-logo wins in this period.",
              label: spec.period === "quarter" ? "Deals closed · Quarter" : "Deals closed · Year",
            };
          case "new-opps":
            return {
              url: `/api/tmd/team-new-opps`,
              sticker: "🌱",
              readOnly: false,
              emptyText: "No opps advanced past Lead Qualified this month.",
              label: "New opps · This month",
            };
          case "pilots":
            return {
              url: `/api/tmd/team-pilots?state=${spec.state}`,
              sticker: "🧪",
              readOnly: spec.state === "ended",
              emptyText:
                spec.state === "active"
                  ? "No pilots currently running."
                  : "No pilots concluded in the last 30 days.",
              label: spec.state === "active" ? "Pilots · Active" : "Pilots · Ended ≤30d",
            };
          case "checkins":
            return {
              url: `/api/tmd/team-checkins`,
              sticker: "📞",
              readOnly: true,
              emptyText: "No check-in calls this quarter.",
              label: "Check-in calls · This quarter",
            };
        }
      })();

      const key = drillKey(spec);
      setDrill({
        key,
        sticker: meta.sticker,
        label: meta.label,
        count: 0,
        loading: true,
        recs: [],
        readOnly: meta.readOnly,
        emptyText: meta.emptyText,
      });
      try {
        const r = await crmApi.get<{
          label: string;
          count: number;
          recommendations?: TeamBriefingRec[];
          groups?: CheckinRepGroup[];
        }>(meta.url);
        setDrill({
          key,
          sticker: meta.sticker,
          label: r.label || meta.label,
          count: r.count,
          loading: false,
          recs: r.recommendations || [],
          readOnly: meta.readOnly,
          emptyText: meta.emptyText,
          // Check-ins come back grouped by rep, not as a flat opp list.
          checkinGroups: spec.kind === "checkins" ? r.groups || [] : undefined,
        });
      } catch {
        setDrill({
          key,
          sticker: meta.sticker,
          label: "Couldn't load this list",
          count: 0,
          loading: false,
          recs: [],
          readOnly: meta.readOnly,
          emptyText: meta.emptyText,
        });
      }
    },
    [crmApi]
  );

  const closeDrill = useCallback(() => setDrill(null), []);

  // Lazy contact lookup for the check-in drill-down: fetch phone/email for a
  // rep's contacts only when that rep row is expanded (the team can have
  // hundreds of check-ins a quarter, so we never fetch them all up front).
  const fetchCheckinContacts = useCallback(
    async (ids: string[]): Promise<Record<string, CheckinContactInfo>> => {
      if (ids.length === 0) return {};
      try {
        const r = await crmApi.get<{ contacts: Record<string, CheckinContactInfo> }>(
          `/api/tmd/team-checkin-contacts?ids=${encodeURIComponent(ids.join(","))}`
        );
        return r.contacts || {};
      } catch {
        return {};
      }
    },
    [crmApi]
  );

  const sendEmail = useCallback(
    async (oppId: string, force: boolean = false) => {
      const cur = emailState[oppId];
      if (!cur || cur.status !== "draft") return;
      const subject = (cur.subject || "").trim();
      const body_plain = (cur.body_plain || "").trim();
      if (!subject || !body_plain) {
        setEmailField(oppId, { status: "draft", error: "Subject and body are required" });
        return;
      }
      setEmailField(oppId, { status: "sending", error: undefined });
      try {
        const payload: Record<string, unknown> = {
          opp_id: oppId,
          subject,
          body_html: cur.body_html || `<p>${body_plain.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>")}</p>`,
          body_plain,
        };
        if (cur.customer) {
          // Free-typed lines; the server splits on commas and checks format
          // + Do Not Disturb (PHOTON-118).
          payload.to_emails = [cur.to_line ?? ""];
          if (cur.cc_line?.trim()) payload.cc_emails = [cur.cc_line];
          if (cur.bcc_line?.trim()) payload.bcc_emails = [cur.bcc_line];
        } else if (cur.to_email) {
          payload.to_email = cur.to_email;
        }
        // CCs the draft itself carried ride along in cc_preserve (trusted)
        // so the merge can't silently drop a configured CC.
        if (cur.cc_emails?.length) payload.cc_preserve = cur.cc_emails;
        Object.assign(payload, impersonateBody);
        if (force || cur.overridden) payload.force = true;
        await crmApi.post("/api/tmd/actions/email-send", payload);
        setEmailField(oppId, { status: "sent" });
      } catch (e) {
        setEmailField(oppId, { status: "draft", error: friendlyApiError(e) });
      }
    },
    [crmApi, impersonate, emailState, setEmailField],
  );

  // Create the email as a draft in the LERT's own Outlook (PHOTON-117).
  // Order matters: the server runs every send-time gate (recipients, DND,
  // 72h) BEFORE the draft exists; then the portal creates it via Graph with
  // the user's own token; then the server records "Photon Drafted" on the
  // contact. If that last step fails the draft still exists — say so and
  // offer a retry rather than pretending nothing happened.
  const outlookTarget = (key: string, contactId?: string): Record<string, string> =>
    contactId ? { task_id: key, contact_id: contactId } : { opp_id: key };

  const recordOutlookDraft = useCallback(
    async (key: string, contactId?: string) => {
      const cur = emailState[key];
      if (!cur) return;
      try {
        await crmApi.post("/api/tmd/actions/outlook-draft/record", {
          ...outlookTarget(key, contactId),
          to_emails: [cur.to_line ?? ""],
          cc_emails: [cur.cc_line ?? ""],
          bcc_emails: [cur.bcc_line ?? ""],
          cc_preserve: cur.cc_emails ?? [],
          subject: (cur.subject || "").trim(),
          body_plain: (cur.body_plain || "").trim(),
          outlook_message_id: cur.drafted_id,
          ...impersonateBody,
        });
        setEmailField(key, { record_error: undefined });
      } catch (e) {
        setEmailField(key, { record_error: friendlyApiError(e) });
      }
    },
    [crmApi, impersonate, emailState, setEmailField],
  );

  const draftInOutlook = useCallback(
    async (key: string, contactId?: string, force: boolean = false) => {
      const cur = emailState[key];
      if (!cur || cur.status !== "draft") return;
      const subject = (cur.subject || "").trim();
      const body_plain = (cur.body_plain || "").trim();
      if (!subject || !body_plain) {
        setEmailField(key, { status: "draft", error: "Subject and body are required" });
        return;
      }
      setEmailField(key, { status: "sending", error: undefined });
      const payload = {
        ...outlookTarget(key, contactId),
        to_emails: [cur.to_line ?? ""],
        cc_emails: [cur.cc_line ?? ""],
        bcc_emails: [cur.bcc_line ?? ""],
        cc_preserve: cur.cc_emails ?? [],
        subject,
        body_plain,
        ...impersonateBody,
        ...(force || cur.overridden ? { force: true } : {}),
      };
      let draft: { id: string; webLink: string };
      try {
        const checked = await crmApi.post<{ to_emails: string[]; cc_emails: string[]; bcc_emails: string[] }>(
          "/api/tmd/actions/outlook-draft/check",
          payload,
        );
        draft = await createOutlookDraft({
          to: checked.to_emails,
          cc: checked.cc_emails,
          bcc: checked.bcc_emails,
          subject,
          bodyPlain: body_plain,
        });
      } catch (e) {
        setEmailField(key, { status: "draft", error: friendlyApiError(e) });
        return;
      }
      // Don't open a tab: Graph's only link is Outlook on the web, and LERTs
      // work in desktop Outlook, where the draft appears in Drafts anyway.
      setEmailField(key, { status: "drafted", drafted_link: draft.webLink, drafted_id: draft.id });
      try {
        await crmApi.post("/api/tmd/actions/outlook-draft/record", {
          ...payload,
          outlook_message_id: draft.id,
        });
      } catch (e) {
        setEmailField(key, { record_error: friendlyApiError(e) });
      }
    },
    [crmApi, impersonate, emailState, setEmailField, createOutlookDraft],
  );

  const cancelEmail = useCallback((oppId: string) => {
    setEmailState((prev) => {
      const next = { ...prev };
      delete next[oppId];
      return next;
    });
  }, []);

  const dismissFlag = useCallback(
    async (flag: Contradiction) => {
      setFlagState((prev) => ({ ...prev, [flag.id]: { status: "pending" } }));
      try {
        const payload: Record<string, string> = {
          flag_id: flag.id,
          opp_id: flag.opp_id,
        };
        Object.assign(payload, impersonateBody);
        await crmApi.post("/api/tmd/actions/dismiss-flag", payload);
        setFlagState((prev) => ({ ...prev, [flag.id]: { status: "dismissed" } }));
      } catch (e) {
        setFlagState((prev) => ({
          ...prev,
          [flag.id]: {
            status: "pending",
            error: e instanceof Error ? e.message : String(e),
          },
        }));
      }
    },
    [crmApi, impersonate],
  );

  const actOnProposal = useCallback(
    async (proposal: Proposal, action: "field-update" | "dismiss") => {
      setProposalState((prev) => ({ ...prev, [proposal.id]: { status: "pending" } }));
      try {
        // Approve sends the full proposal snapshot — server validates auth +
        // allowlist + writes exactly what the user saw. No regen matching,
        // so non-deterministic Gemini inferences (close-date hints) no longer
        // drift between briefing and click. Dismiss keeps the slim body.
        const body: Record<string, unknown> =
          action === "field-update"
            ? {
                proposal_id: proposal.id,
                opp_id: proposal.opp_id,
                field_id: proposal.field_id,
                field_name: proposal.field_name,
                proposed_value: proposal.proposed_value,
                proposed_value_display: proposal.proposed_value_display,
                target: proposal.target ?? "custom_field",
                value_type: proposal.value_type,
                source_summary: proposal.source_summary,
              }
            : {
                proposal_id: proposal.id,
                opp_id: proposal.opp_id,
              };
        Object.assign(body, impersonateBody);
        await crmApi.post(`/api/tmd/actions/${action}`, body);
        setProposalState((prev) => ({
          ...prev,
          [proposal.id]: { status: action === "field-update" ? "approved" : "dismissed" },
        }));
      } catch (e) {
        const errStr = e instanceof Error ? e.message : String(e);
        // 409 = the proposal_id went stale between briefing load and approve
        // click. Usually happens when the underlying generator's proposed
        // value changed (prompt tweak, Gemini non-determinism). Auto-refresh
        // the briefing so the user sees the live proposal and can retry.
        const is409 = /HTTP 409/i.test(errStr);
        setProposalState((prev) => ({
          ...prev,
          [proposal.id]: {
            status: "pending",
            error: is409
              ? "This proposal was out of date — refreshing briefing. Click Approve on the updated suggestion."
              : errStr,
          },
        }));
        if (is409) {
          try {
            await loadAll();
          } catch {
            // loadAll has its own error state; don't overwrite the 409 message
          }
        }
      }
    },
    [crmApi, impersonate, loadAll],
  );

  // Close-confidence cards (PHOTON-74): per-card answer state + optional why.
  const [confidenceState, setConfidenceState] = useState<
    Record<string, { status: "pending" | "answered" | "skipped" | "error"; level?: string; error?: string }>
  >({});
  const [confidenceWhy, setConfidenceWhy] = useState<Record<string, string>>({});

  const answerConfidence = useCallback(
    async (check: ConfidenceCheck, level: "high" | "medium" | "low" | "skip") => {
      setConfidenceState((prev) => ({ ...prev, [check.opp_id]: { status: "pending", level } }));
      try {
        const body: Record<string, unknown> = {
          opp_id: check.opp_id,
          level,
          expected_close_at_answer: check.expected_close,
        };
        const why = (confidenceWhy[check.opp_id] || "").trim();
        if (why && level !== "skip") body.why = why;
        Object.assign(body, impersonateBody);
        await crmApi.post("/api/tmd/actions/confidence-note", body);
        setConfidenceState((prev) => ({
          ...prev,
          [check.opp_id]: { status: level === "skip" ? "skipped" : "answered", level },
        }));
      } catch (e) {
        setConfidenceState((prev) => ({
          ...prev,
          [check.opp_id]: {
            status: "error",
            level,
            error: e instanceof Error ? e.message : String(e),
          },
        }));
      }
    },
    [crmApi, impersonate, confidenceWhy],
  );

  const actOnPmSuggestion = useCallback(
    async (oppId: string, suggestion: PmPhotonSuggest, action: "approve" | "dismiss") => {
      const sid = suggestion.id ?? "";
      if (!sid) return;
      setProposalState((prev) => ({ ...prev, [sid]: { status: "pending" } }));
      try {
        const body: Record<string, unknown> = {
          suggestion_id: sid,
          suggestion_kind: suggestion.kind,
          opp_id: oppId,
          action,
        };
        if (action === "approve" && suggestion.kind === "rename") {
          body.suggested_name = suggestion.suggested_name;
        }
        await crmApi.post("/api/tmd/pm/suggestion-action", body);
        setProposalState((prev) => ({
          ...prev,
          [sid]: { status: action === "approve" ? "approved" : "dismissed" },
        }));
      } catch (e) {
        const errStr = e instanceof Error ? e.message : String(e);
        const is409 = /HTTP 409/i.test(errStr);
        setProposalState((prev) => ({
          ...prev,
          [sid]: {
            status: "pending",
            error: is409
              ? "Suggestion is out of date — refreshing. Click again on the updated card."
              : errStr,
          },
        }));
        if (is409) {
          try {
            await loadAll();
          } catch {
            // loadAll handles its own error state
          }
        }
      }
    },
    [crmApi, loadAll],
  );

  const completeTask = useCallback(
    async (taskId: string, contactId: string) => {
      // Location-scoped completion works without a contact, so only require the
      // task id; contact_id is still sent (may be empty) for the audit note.
      if (!taskId) return;
      setTaskState((prev) => ({ ...prev, [taskId]: { status: "completing" } }));
      try {
        const payload: Record<string, unknown> = { task_id: taskId, contact_id: contactId };
        Object.assign(payload, impersonateBody);
        await crmApi.post("/api/tmd/actions/complete-task", payload);
        setTaskState((prev) => ({ ...prev, [taskId]: { status: "completed" } }));
      } catch (e) {
        setTaskState((prev) => ({
          ...prev,
          [taskId]: { status: "error", error: e instanceof Error ? e.message : String(e) },
        }));
      }
    },
    [crmApi, impersonate],
  );

  // Task-driven email + talk-track handlers key their state into the SAME
  // emailState/trackState maps used by opp-driven handlers — we just key by
  // task_id instead of opp_id. The two ID spaces don't collide (GHL Task IDs
  // and Opp IDs are disjoint) and sharing state means the existing cancel /
  // error shapes Just Work for task cards too.
  const startTaskDraftEmail = useCallback(
    async (taskId: string, contactId: string, force: boolean = false) => {
      if (!taskId || !contactId) return;
      setEmailState((prev) => ({ ...prev, [taskId]: { status: "drafting" } }));
      try {
        const payload: Record<string, unknown> = { task_id: taskId, contact_id: contactId };
        Object.assign(payload, impersonateBody);
        if (force) payload.force = true;
        const resp = await crmApi.post<{
          draft_available: boolean;
          reason?: string;
          recency_block?: boolean;
          can_override?: boolean;
          to_email?: string;
          to_name?: string;
          from_email?: string;
          subject?: string;
          body_plain?: string;
          body_html?: string;
          reasoning?: string;
        }>("/api/tmd/actions/email-draft-task", payload);
        if (!resp.draft_available) {
          setEmailState((prev) => ({
            ...prev,
            [taskId]: {
              status: resp.recency_block ? "recency" : "declined",
              declined_reason: resp.reason || "Drafter declined.",
              can_override: Boolean(resp.can_override),
            },
          }));
          return;
        }
        setEmailState((prev) => ({
          ...prev,
          [taskId]: {
            status: "draft",
            overridden: force,
            to_email: resp.to_email || "",
            to_name: resp.to_name || "",
            from_email: resp.from_email || "",
            customer: true,
            to_line: resp.to_email || "",
            cc_line: "",
            bcc_line: "",
            subject: resp.subject || "",
            body_plain: resp.body_plain || "",
            body_html: resp.body_html || "",
            reasoning: resp.reasoning || "",
          },
        }));
      } catch (e) {
        setEmailState((prev) => ({
          ...prev,
          [taskId]: { status: "error", error: e instanceof Error ? e.message : String(e) },
        }));
      }
    },
    [crmApi, impersonate],
  );

  const sendTaskEmail = useCallback(
    async (taskId: string, contactId: string, force: boolean = false) => {
      const cur = emailState[taskId];
      if (!cur || cur.status !== "draft") return;
      const subject = (cur.subject || "").trim();
      const body_plain = (cur.body_plain || "").trim();
      if (!subject || !body_plain) {
        setEmailField(taskId, { status: "draft", error: "Subject and body are required" });
        return;
      }
      setEmailField(taskId, { status: "sending", error: undefined });
      try {
        const payload: Record<string, unknown> = {
          task_id: taskId,
          contact_id: contactId,
          subject,
          body_html: cur.body_html || `<p>${body_plain.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>")}</p>`,
          body_plain,
        };
        payload.to_emails = [cur.to_line ?? ""];
        if (cur.cc_line?.trim()) payload.cc_emails = [cur.cc_line];
        if (cur.bcc_line?.trim()) payload.bcc_emails = [cur.bcc_line];
        if (cur.cc_emails?.length) payload.cc_preserve = cur.cc_emails;
        Object.assign(payload, impersonateBody);
        if (force || cur.overridden) payload.force = true;
        await crmApi.post("/api/tmd/actions/email-send-task", payload);
        setEmailField(taskId, { status: "sent" });
      } catch (e) {
        setEmailField(taskId, { status: "draft", error: friendlyApiError(e) });
      }
    },
    [crmApi, impersonate, emailState, setEmailField],
  );

  const startTaskTalkTrack = useCallback(
    async (taskId: string, contactId: string, force: boolean = false) => {
      if (!taskId || !contactId) return;
      setTrackState((prev) => ({ ...prev, [taskId]: { status: "loading" } }));
      try {
        const payload: Record<string, unknown> = { task_id: taskId, contact_id: contactId };
        Object.assign(payload, impersonateBody);
        if (force) payload.force = true;
        const resp = await crmApi.post<{
          prep_available: boolean;
          reason?: string;
          recency_block?: boolean;
          can_override?: boolean;
          call_type?: string;
          context_summary?: string;
          opener?: string;
          questions?: string[];
          listen_for?: string[];
          close?: string;
          reasoning?: string;
        }>("/api/tmd/actions/talk-track-task", payload);
        if (!resp.prep_available) {
          setTrackState((prev) => ({
            ...prev,
            [taskId]: {
              status: resp.recency_block ? "recency" : "declined",
              declined_reason: resp.reason || "Generator declined.",
              can_override: Boolean(resp.can_override),
            },
          }));
          return;
        }
        setTrackState((prev) => ({
          ...prev,
          [taskId]: {
            status: "available",
            call_type: resp.call_type,
            context_summary: resp.context_summary,
            opener: resp.opener,
            questions: resp.questions,
            listen_for: resp.listen_for,
            close: resp.close,
            reasoning: resp.reasoning,
          },
        }));
      } catch (e) {
        setTrackState((prev) => ({
          ...prev,
          [taskId]: { status: "error", error: e instanceof Error ? e.message : String(e) },
        }));
      }
    },
    [crmApi, impersonate],
  );

  const snoozeTask = useCallback(
    async (taskId: string, contactId: string, days: number = 3) => {
      if (!taskId || !contactId) return;
      setTaskState((prev) => ({ ...prev, [taskId]: { status: "snoozing" } }));
      try {
        const payload: Record<string, unknown> = { task_id: taskId, contact_id: contactId, days };
        Object.assign(payload, impersonateBody);
        await crmApi.post("/api/tmd/actions/snooze-task", payload);
        setTaskState((prev) => ({ ...prev, [taskId]: { status: "snoozed" } }));
      } catch (e) {
        setTaskState((prev) => ({
          ...prev,
          [taskId]: { status: "error", error: e instanceof Error ? e.message : String(e) },
        }));
      }
    },
    [crmApi, impersonate],
  );

  if (!crmApi.isConfigured()) {
    return (
      <div className={styles.container}>
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>CRM Service Not Configured</MessageBarTitle>
            crmServiceUrl is empty. Check config.json.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  if (loading && !me) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading Tackle My Day..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  const renderProposals = (proposals: Proposal[] | undefined) => {
    if (!proposals || proposals.length === 0) return null;
    return (
      <>
        {proposals.map((p) => {
          const state = proposalState[p.id];
          const busy = state?.status === "pending";
          const settled = state?.status === "approved" || state?.status === "dismissed";
          const displayValue = p.proposed_value_display || p.proposed_value;
          const settledMessage =
            state?.status === "approved"
              ? `Done — wrote "${p.field_name} = ${displayValue}" and logged Photon Set:.`
              : state?.status === "dismissed"
                ? "Dismissed — logged User Dismissed:."
                : "";
          return (
            <div key={p.id} className={styles.proposalBlock}>
              <div className={styles.proposalLabel}>
                <Sparkle16Regular className={styles.askPhotonIcon} /> Photon suggests
              </div>
              <div className={styles.proposalHeadline}>{p.headline}</div>
              <div className={styles.proposalReason}>{p.reason}</div>
              {!settled ? (
                <div className={styles.proposalActions}>
                  <Button
                    appearance="primary"
                    size="small"
                    disabled={busy}
                    onClick={() => actOnProposal(p, "field-update")}
                  >
                    {busy ? <Spinner size="tiny" /> : "Approve"}
                  </Button>
                  <Button
                    appearance="secondary"
                    size="small"
                    disabled={busy}
                    onClick={() => actOnProposal(p, "dismiss")}
                  >
                    Dismiss
                  </Button>
                  {state?.error && (
                    <span className={styles.proposalError}>{state.error}</span>
                  )}
                </div>
              ) : (
                <div className={styles.proposalSuccess}>{settledMessage}</div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const renderConfidenceChecks = (checks: ConfidenceCheck[] | undefined) => {
    if (!checks || checks.length === 0) return null;
    return (
      <>
        <Text weight="semibold" size={400}>Close-confidence check-ins</Text>
        {checks.map((c) => {
          const state = confidenceState[c.opp_id];
          const busy = state?.status === "pending";
          if (state?.status === "answered" || state?.status === "skipped") {
            return (
              <div key={c.opp_id} className={styles.ccCard}>
                <div className={styles.ccSubline}>{c.opp_name}</div>
                <div className={styles.proposalSuccess}>
                  {state.status === "answered"
                    ? `Recorded — Rep Confidence: ${String(state.level).toUpperCase()} logged on the contact.`
                    : "Skipped — will come back in a few days."}
                </div>
              </div>
            );
          }
          return (
            <div key={c.opp_id} className={styles.ccCard}>
              <div className={styles.proposalLabel}>
                <Sparkle16Regular className={styles.askPhotonIcon} /> Photon check-in
              </div>
              <div className={styles.ccSubline}>
                {c.opp_name}
                {c.stage ? ` · ${c.stage}` : ""}
              </div>
              <div className={styles.proposalHeadline}>{c.question}</div>
              {c.last_answer && (
                <div className={styles.ccLastAnswer}>
                  Last answer: {c.last_answer.level} on {new Date(c.last_answer.answered_at).toLocaleDateString()}
                  {c.last_answer.expected_close_at_answer !== c.expected_close
                    ? ` (expected close has since moved from ${c.last_answer.expected_close_at_answer})`
                    : ""}
                </div>
              )}
              <Textarea
                className={styles.ccWhy}
                placeholder="Why? (optional, one line)"
                value={confidenceWhy[c.opp_id] || ""}
                rows={1}
                resize="none"
                disabled={busy}
                onChange={(_, data) =>
                  setConfidenceWhy((prev) => ({ ...prev, [c.opp_id]: data.value }))
                }
              />
              <div className={styles.proposalActions}>
                <Button appearance="primary" size="small" disabled={busy}
                  onClick={() => answerConfidence(c, "high")}>
                  {busy && state?.level === "high" ? <Spinner size="tiny" /> : "High"}
                </Button>
                <Button appearance="secondary" size="small" disabled={busy}
                  onClick={() => answerConfidence(c, "medium")}>
                  {busy && state?.level === "medium" ? <Spinner size="tiny" /> : "Medium"}
                </Button>
                <Button appearance="secondary" size="small" disabled={busy}
                  onClick={() => answerConfidence(c, "low")}>
                  {busy && state?.level === "low" ? <Spinner size="tiny" /> : "Low"}
                </Button>
                <Button appearance="subtle" size="small" disabled={busy}
                  onClick={() => answerConfidence(c, "skip")}>
                  Skip
                </Button>
                {state?.error && <span className={styles.proposalError}>{state.error}</span>}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  // Callbacks shape lets opp and task cards share the render logic —
  // only the endpoint wiring and state-key semantics differ per caller.
  interface TrackCallbacks {
    retryWithForce: () => void;
    close: () => void;
  }
  interface EmailCallbacks {
    retryWithForce: () => void;
    send: () => void;
    sendWithForce: () => void;
    outlook: () => void;
    outlookWithForce: () => void;
    retryRecord: () => void;
    cancel: () => void;
  }

  const renderTalkTrackBody = (key: string, cbs: TrackCallbacks) => {
    const s = trackState[key];
    if (!s) return null;
    if (s.status === "loading") {
      return (
        <div className={styles.trackContainer}>
          <div className={styles.trackLabel}>📞 Call prep — generating...</div>
          <Spinner size="tiny" />
        </div>
      );
    }
    if (s.status === "declined" || s.status === "recency") {
      const isRecency = s.status === "recency";
      return (
        <div className={styles.trackContainer}>
          <div className={styles.trackLabel}>📞 Call prep — {isRecency ? "recent contact" : "declined"}</div>
          <div className={styles.draftDeclined}>{s.declined_reason}</div>
          <div className={styles.draftActions}>
            {isRecency && s.can_override && (
              <Button
                appearance="primary"
                size="small"
                onClick={cbs.retryWithForce}
              >
                Generate anyway
              </Button>
            )}
            <Button appearance="secondary" size="small" onClick={cbs.close}>Close</Button>
          </div>
        </div>
      );
    }
    if (s.status === "error") {
      return (
        <div className={styles.trackContainer}>
          <div className={styles.trackLabel}>📞 Call prep — error</div>
          <div className={styles.draftError}>{s.error}</div>
          <div className={styles.draftActions}>
            <Button appearance="secondary" size="small" onClick={cbs.close}>Close</Button>
          </div>
        </div>
      );
    }
    // available
    return (
      <div className={styles.trackContainer}>
        <div className={styles.trackLabel}>📞 Call prep (your voice)</div>
        {s.call_type && (
          <div className={styles.trackCallType}>Call type: {s.call_type.replace(/_/g, " ")}</div>
        )}
        {s.context_summary && (
          <div className={styles.trackSection}>
            <div className={styles.trackSectionHeader}>Context</div>
            <div className={styles.trackText}>{s.context_summary}</div>
          </div>
        )}
        {s.opener && (
          <div className={styles.trackSection}>
            <div className={styles.trackSectionHeader}>Opener</div>
            <div className={styles.trackText}>{s.opener}</div>
          </div>
        )}
        {s.questions && s.questions.length > 0 && (
          <div className={styles.trackSection}>
            <div className={styles.trackSectionHeader}>Ask</div>
            <ul className={styles.trackList}>
              {s.questions.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </div>
        )}
        {s.listen_for && s.listen_for.length > 0 && (
          <div className={styles.trackSection}>
            <div className={styles.trackSectionHeader}>Listen for</div>
            <ul className={styles.trackList}>
              {s.listen_for.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </div>
        )}
        {s.close && (
          <div className={styles.trackSection}>
            <div className={styles.trackSectionHeader}>Close</div>
            <div className={styles.trackText}>{s.close}</div>
          </div>
        )}
        {s.reasoning && <div className={styles.draftReasoning}>Why this shape: {s.reasoning}</div>}
        <div className={styles.draftActions}>
          <Button
            appearance="secondary"
            size="small"
            onClick={() => {
              const text = [
                s.call_type ? `Call type: ${s.call_type.replace(/_/g, " ")}` : "",
                s.context_summary ? `Context: ${s.context_summary}` : "",
                s.opener ? `Opener: ${s.opener}` : "",
                s.questions?.length ? `Ask:\n- ${s.questions.join("\n- ")}` : "",
                s.listen_for?.length ? `Listen for:\n- ${s.listen_for.join("\n- ")}` : "",
                s.close ? `Close: ${s.close}` : "",
              ].filter(Boolean).join("\n\n");
              navigator.clipboard?.writeText(text).catch(() => {});
            }}
          >
            Copy
          </Button>
          <Button appearance="secondary" size="small" onClick={cbs.close}>Close</Button>
        </div>
      </div>
    );
  };

  const renderTalkTrack = (oppId: string) =>
    renderTalkTrackBody(oppId, {
      retryWithForce: () => startTalkTrack(oppId, true),
      close: () => closeTalkTrack(oppId),
    });

  const renderTaskTalkTrack = (taskId: string, contactId: string) =>
    renderTalkTrackBody(taskId, {
      retryWithForce: () => startTaskTalkTrack(taskId, contactId, true),
      close: () => closeTalkTrack(taskId),
    });

  const renderEmailDraftBody = (key: string, cbs: EmailCallbacks) => {
    const s = emailState[key];
    if (!s) return null;
    // Pipeline-wide profiles (e.g. existing_customer / Tatiana) draft
    // internal coordination emails — relabel from the LERT-flavored
    // "Sales Rep draft" so the header reads sensibly.
    const draftKind =
      me?.profile?.bucket_strategy === "pipeline_wide" ||
      me?.profile?.bucket_strategy === "lert_rollup"
        ? "Internal nudge"
        : "Sales Rep";
    if (s.status === "drafting") {
      return (
        <div className={styles.draftContainer}>
          <div className={styles.draftLabel}>{draftKind} draft — drafting...</div>
          <Spinner size="tiny" />
        </div>
      );
    }
    if (s.status === "declined" || s.status === "recency") {
      const isRecency = s.status === "recency";
      return (
        <div className={styles.draftContainer}>
          <div className={styles.draftLabel}>{draftKind} draft — {isRecency ? "recent contact" : "declined"}</div>
          <div className={styles.draftDeclined}>{s.declined_reason}</div>
          <div className={styles.draftActions}>
            {isRecency && s.can_override && (
              <Button
                appearance="primary"
                size="small"
                onClick={cbs.retryWithForce}
              >
                Draft anyway
              </Button>
            )}
            <Button appearance="secondary" size="small" onClick={cbs.cancel}>
              Close
            </Button>
          </div>
        </div>
      );
    }
    if (s.status === "error") {
      return (
        <div className={styles.draftContainer}>
          <div className={styles.draftLabel}>{draftKind} draft — error</div>
          <div className={styles.draftError}>{s.error}</div>
          <div className={styles.draftActions}>
            <Button appearance="secondary" size="small" onClick={cbs.cancel}>
              Close
            </Button>
          </div>
        </div>
      );
    }
    if (s.status === "drafted") {
      return (
        <div className={styles.draftContainer}>
          <div className={styles.draftLabel}>{draftKind} draft — in your Outlook</div>
          <div className={styles.draftSuccess}>
            <strong>Draft saved to your Outlook Drafts folder.</strong> Open Outlook to review
            and send it; replies will come to your inbox.
            {s.drafted_link && (
              <>
                {" "}(
                <a href={s.drafted_link} target="_blank" rel="noopener noreferrer">
                  Open in Outlook on the web
                </a>
                )
              </>
            )}
          </div>
          {s.record_error ? (
            <div className={styles.draftActions}>
              <span className={styles.draftError}>
                The draft exists, but logging it on the contact failed: {s.record_error}
              </span>
              <Button appearance="secondary" size="small" onClick={cbs.retryRecord}>
                Retry logging
              </Button>
            </div>
          ) : (
            <div className={styles.draftReasoning}>Logged "Photon Drafted" on the contact.</div>
          )}
        </div>
      );
    }
    if (s.status === "sent") {
      return (
        <div className={styles.draftContainer}>
          <div className={styles.draftLabel}>{draftKind} draft — sent</div>
          <div className={styles.draftSuccess}>
            Sent to {s.to_email}. Logged Photon Sent: on the contact.
          </div>
        </div>
      );
    }
    // draft or sending
    const busy = s.status === "sending";
    // Customer emails go to the LERT's Outlook when enabled (PHOTON-117);
    // internal nudges always send via GHL.
    const useOutlook = outlookEnabled && Boolean(s.customer);
    const canSubmit =
      Boolean(s.subject?.trim()) &&
      Boolean(s.body_plain?.trim()) &&
      (!s.customer || Boolean(s.to_line?.trim()));
    // The 72h gate comes back as a 429 whose message mentions the window —
    // simpler than parsing structured errors.
    const recencyErr = Boolean(s.error && /wait until|hours ago|recent/i.test(s.error));
    return (
      <div className={styles.draftContainer}>
        <div className={styles.draftLabel}>{draftKind} draft (your voice — edit before sending)</div>
        {useOutlook ? (
          <div className={styles.draftFieldRow}>
            <div className={styles.draftFieldLabel}>From</div>
            <div style={{ fontSize: "13px", color: tokens.colorNeutralForeground2 }}>
              Your own Outlook mailbox
              {me?.is_impersonating &&
                " — note: you're viewing as someone else, so the draft goes to YOUR Drafts, not theirs"}
              {s.from_email ? ` (Send via CRM sends from ${s.from_email})` : ""}
            </div>
          </div>
        ) : (
          s.from_email && (
            <div className={styles.draftFieldRow}>
              <div className={styles.draftFieldLabel}>From</div>
              <div style={{ fontSize: "13px", color: tokens.colorNeutralForeground2 }}>
                {s.from_email}
              </div>
            </div>
          )
        )}
        {s.customer ? (
          <>
            {(["to", "cc", "bcc"] as const).map((line) => {
              const field = `${line}_line` as "to_line" | "cc_line" | "bcc_line";
              return (
                <div key={line} className={styles.draftFieldRow}>
                  <label className={styles.draftFieldLabel} htmlFor={`${key}-${line}`}>
                    {line === "to" ? "To" : line === "cc" ? "Cc" : "Bcc"}
                  </label>
                  <input
                    id={`${key}-${line}`}
                    type="text"
                    className={styles.draftSmallInput}
                    placeholder={line === "to" ? "" : "Separate addresses with commas"}
                    value={s[field] || ""}
                    onChange={(e) => setEmailField(key, { [field]: e.target.value })}
                    disabled={busy}
                  />
                </div>
              );
            })}
            {(s.to_line || "").split(/[,;\s]+/).filter((x) => x.includes("@")).length > 1 && (
              <div className={styles.draftReasoning}>
                {useOutlook ? "If you use Send via CRM instead of Outlook, " : "Sending via CRM: "}
                the first To address gets the email as To and the others are Cc'd.
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.draftFieldRow}>
              <div className={styles.draftFieldLabel}>To</div>
              <input
                type="email"
                className={styles.draftSmallInput}
                value={s.to_email || ""}
                onChange={(e) => setEmailField(key, { to_email: e.target.value })}
                disabled={busy}
              />
            </div>
            {(s.cc_emails ?? []).length > 0 && (
              <div className={styles.draftFieldRow}>
                <div className={styles.draftFieldLabel}>Cc</div>
                <div className={styles.ccPicker}>
                  {/* CCs the draft itself configured (e.g. a rollup/nudge
                      draft). Locked so a configured recipient can't be dropped. */}
                  {(s.cc_emails ?? []).map((e) => (
                    <span key={e} className={styles.ccLocked} title="Configured for this draft">
                      {e}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        <div className={styles.draftFieldRow}>
          <div className={styles.draftFieldLabel}>Subject</div>
          <input
            type="text"
            className={styles.draftSmallInput}
            value={s.subject || ""}
            onChange={(e) => setEmailField(key, { subject: e.target.value })}
            disabled={busy}
          />
        </div>
        <Textarea
          value={s.body_plain || ""}
          onChange={(_, data) => setEmailField(key, { body_plain: data.value, body_html: undefined })}
          rows={10}
          resize="vertical"
          disabled={busy}
        />
        {s.reasoning && <div className={styles.draftReasoning}>Why this angle: {s.reasoning}</div>}
        <div className={styles.draftActions}>
          {recencyErr ? (
            // The 72h gate fired: offer only the deliberate overrides.
            <>
              {useOutlook && (
                <Button
                  appearance="primary"
                  size="small"
                  disabled={busy || !canSubmit}
                  onClick={cbs.outlookWithForce}
                >
                  {busy ? <Spinner size="tiny" /> : "Create draft anyway"}
                </Button>
              )}
              <Button
                appearance={useOutlook ? "secondary" : "primary"}
                size="small"
                disabled={busy || !canSubmit}
                onClick={cbs.sendWithForce}
              >
                {busy && !useOutlook ? <Spinner size="tiny" /> : useOutlook ? "Send via CRM anyway" : "Send anyway"}
              </Button>
            </>
          ) : (
            <>
              {useOutlook && (
                <Button
                  appearance="primary"
                  size="small"
                  disabled={busy || !canSubmit}
                  onClick={cbs.outlook}
                >
                  {busy ? <Spinner size="tiny" /> : "Create Outlook draft"}
                </Button>
              )}
              <Button
                appearance={useOutlook ? "secondary" : "primary"}
                size="small"
                disabled={busy || !canSubmit}
                onClick={cbs.send}
              >
                {busy && !useOutlook ? <Spinner size="tiny" /> : useOutlook ? "Send via CRM instead" : "Approve & Send"}
              </Button>
            </>
          )}
          <Button appearance="secondary" size="small" disabled={busy} onClick={cbs.cancel}>
            Cancel
          </Button>
          {s.error && <span className={styles.draftError}>{s.error}</span>}
        </div>
      </div>
    );
  };

  const renderEmailDraft = (oppId: string) =>
    renderEmailDraftBody(oppId, {
      retryWithForce: () => startDraftEmail(oppId, true),
      send: () => sendEmail(oppId),
      sendWithForce: () => sendEmail(oppId, true),
      outlook: () => draftInOutlook(oppId),
      outlookWithForce: () => draftInOutlook(oppId, undefined, true),
      retryRecord: () => recordOutlookDraft(oppId),
      cancel: () => cancelEmail(oppId),
    });

  const renderTaskEmailDraft = (taskId: string, contactId: string) =>
    renderEmailDraftBody(taskId, {
      retryWithForce: () => startTaskDraftEmail(taskId, contactId, true),
      send: () => sendTaskEmail(taskId, contactId),
      sendWithForce: () => sendTaskEmail(taskId, contactId, true),
      outlook: () => draftInOutlook(taskId, contactId),
      outlookWithForce: () => draftInOutlook(taskId, contactId, true),
      retryRecord: () => recordOutlookDraft(taskId, contactId),
      cancel: () => cancelEmail(taskId),
    });

  const renderContradictions = (flags: Contradiction[] | undefined) => {
    if (!flags || flags.length === 0) return null;
    return (
      <>
        {flags.map((f) => {
          const state = flagState[f.id];
          const busy = state?.status === "pending";
          const settled = state?.status === "dismissed";
          return (
            <div key={f.id} className={styles.flagBlock}>
              <div className={styles.flagLabel}>⚑ Photon flagged</div>
              <div className={styles.flagSummary}>{f.summary}</div>
              {f.details && <div className={styles.flagDetails}>{f.details}</div>}
              {!settled ? (
                <div className={styles.flagActions}>
                  <Button
                    appearance="secondary"
                    size="small"
                    disabled={busy}
                    onClick={() => dismissFlag(f)}
                  >
                    {busy ? <Spinner size="tiny" /> : "Dismiss"}
                  </Button>
                  {state?.error && <span className={styles.flagError}>{state.error}</span>}
                </div>
              ) : (
                <div className={styles.flagDismissed}>Dismissed — logged User Dismissed:.</div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const renderConfidence = (rec: Recommendation) => {
    if (!rec.confidence) return null;
    const cls =
      rec.confidence === "HIGH"
        ? styles.confidenceHigh
        : rec.confidence === "MEDIUM"
          ? styles.confidenceMedium
          : styles.confidenceLow;
    return (
      <span
        className={`${styles.confidenceBadge} ${cls}`}
        title={rec.confidence === "LOW" ? rec.confidence_reason || "Low confidence" : rec.confidence}
      >
        {CONFIDENCE_LABEL[rec.confidence]}
      </span>
    );
  };

  const renderNote = (note: NoteItem, key: string) => {
    const intake = parseAmandaIntake(note.body);
    if (intake) {
      return (
        <div key={key} className={styles.intakeCard}>
          <div className={styles.intakeTitle}>
            Amanda intake · {note.date ? new Date(note.date).toLocaleDateString() : ""}
          </div>
          {intake.fields.length > 0 && (
            <div className={styles.intakeFieldGrid}>
              {intake.fields.map((f, i) => (
                <React.Fragment key={i}>
                  <div className={styles.intakeLabel}>{f.label}</div>
                  <div>{f.value}</div>
                </React.Fragment>
              ))}
            </div>
          )}
          {intake.callNotes && (
            <div className={styles.intakeCallNotes}>{intake.callNotes}</div>
          )}
        </div>
      );
    }
    return (
      <div key={key} className={styles.noteItem}>
        <div className={styles.noteMeta}>
          {note.date && new Date(note.date).toLocaleDateString()}
          {note.date && note.user && " · "}
          {note.user}
        </div>
        <div className={styles.noteBody}>{note.body}</div>
      </div>
    );
  };

  const renderNotes = (rec: Recommendation) => {
    const oppId = rec.opp_id || "";
    const all = rec.notes || [];
    const human = all.filter((n) => !n.is_photon);
    const photon = all.filter((n) => n.is_photon);
    const photonOpen = photonExpanded.has(oppId);
    return (
      <div className={styles.notesBlock}>
        {human.length === 0 ? (
          <div className={styles.noNotes}>No notes in the opportunity window.</div>
        ) : (
          human.map((n, i) => renderNote(n, `h-${i}`))
        )}
        {photon.length > 0 && (
          <div className={styles.photonBlock}>
            <button
              className={styles.photonToggle}
              onClick={() => togglePhotonExpanded(oppId)}
              aria-expanded={photonOpen}
            >
              {photonOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
              Photon activity ({photon.length})
            </button>
            {photonOpen && (
              <div style={{ marginTop: tokens.spacingVerticalS, display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
                {photon.map((n, i) => renderNote(n, `p-${i}`))}
              </div>
            )}
          </div>
        )}
        {(() => {
          const ns = noteState[oppId] || { open: false, text: "", busy: false };
          if (!ns.open) {
            const emailOpen = Boolean(emailState[oppId]);
            return (
              <>
                <div className={styles.notesActions}>
                  <button
                    type="button"
                    className={styles.addNoteToggle}
                    onClick={() => setNoteField(oppId, { open: true })}
                  >
                    + Add a note
                  </button>
                  <button
                    type="button"
                    className={styles.emailLink}
                    onClick={() => startDraftEmail(oppId, false, rec)}
                    disabled={emailOpen}
                    title={
                      me?.profile?.bucket_strategy === "lert_rollup"
                        ? "Draft an internal nudge to the owning rep (Greg + Dale Cc'd)"
                        : "Have Photon draft an email"
                    }
                  >
                    <Mail16Regular />{" "}
                    {me?.profile?.bucket_strategy === "lert_rollup" ? "Nudge rep" : "Draft email"}
                  </button>
                  {/* sales_admin_lert: always-available Finance/PEA nudge to
                      Schuyler (Greg + Dale Cc'd), independent of the briefing's
                      recommended target. Spec §5. */}
                  {me?.profile?.bucket_strategy === "lert_rollup" && (
                    <button
                      type="button"
                      className={styles.emailLink}
                      onClick={() => startDraftEmail(oppId, false, rec, "schuyler")}
                      disabled={emailOpen}
                      title="Draft a finance/PEA nudge to Schuyler (Greg + Dale Cc'd)"
                    >
                      <Mail16Regular /> Finance/PEA nudge
                    </button>
                  )}
                  {me?.profile?.bucket_strategy !== "pipeline_wide" &&
                    me?.profile?.bucket_strategy !== "pm_by_state" &&
                    me?.profile?.bucket_strategy !== "lert_rollup" && (
                    <button
                      type="button"
                      className={styles.emailLink}
                      onClick={() => startTalkTrack(oppId)}
                      disabled={Boolean(trackState[oppId])}
                      title="Generate a call prep doc for the primary contact"
                    >
                      <Call16Regular /> Talk track
                    </button>
                  )}
                  {onAskAboutOpp && (
                    <button
                      type="button"
                      className={styles.askPhoton}
                      onClick={() => onAskAboutOpp(buildAskPrompt(rec), buildAskTitle(rec))}
                      title="Ask Photon about this opportunity"
                    >
                      <Sparkle16Regular className={styles.askPhotonIcon} /> Ask Photon
                    </button>
                  )}
                  {oppId && (
                    <a
                      className={styles.openInGhl}
                      href={`${GHL_BASE}/opportunities/${oppId}?tab=Opportunity+Details`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Open16Regular /> Open in CRM
                    </a>
                  )}
                </div>
                {renderTalkTrack(oppId)}
                {renderEmailDraft(oppId)}
              </>
            );
          }
          return (
            <div className={styles.addNoteBlock}>
              <Textarea
                value={ns.text}
                onChange={(_, data) => setNoteField(oppId, { text: data.value })}
                placeholder="Write a note on this contact..."
                resize="vertical"
                rows={3}
                disabled={ns.busy}
              />
              <div className={styles.addNoteActions}>
                <Button
                  appearance="primary"
                  size="small"
                  disabled={ns.busy || !ns.text.trim()}
                  onClick={() => saveNote(oppId)}
                >
                  {ns.busy ? <Spinner size="tiny" /> : "Save note"}
                </Button>
                <Button
                  appearance="secondary"
                  size="small"
                  disabled={ns.busy}
                  onClick={() => setNoteField(oppId, { open: false, text: "", error: undefined })}
                >
                  Cancel
                </Button>
                {ns.error && <span className={styles.addNoteError}>{ns.error}</span>}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const renderPmSuggests = (oppId: string, suggests: PmPhotonSuggest[] | undefined) => {
    if (!suggests || suggests.length === 0) return null;
    // "Photon Suggests" carries the contract that an action is below it.
    // Filter out non-actionable items (stalls, update_fields without an
    // Approve flow, template-only rename fallbacks). Their data still rides
    // along to Gemini via the briefing payload; Gemini's reasoning sentence
    // narrates them already, so a separate block would be duplicative.
    const actionable = suggests.filter((s) => s.approvable === true);
    if (actionable.length === 0) return null;
    return (
      <>
        {actionable.map((s, idx) => {
          const sid = s.id ?? `${oppId}-${idx}`;
          const st = proposalState[sid];
          const busy = st?.status === "pending";
          const settled = st?.status === "approved" || st?.status === "dismissed";
          const showActions =
            s.approvable &&
            (s.kind === "rename" || s.kind === "update_fields");
          return (
            <div key={sid} className={styles.proposalBlock}>
              <div className={styles.proposalLabel}>
                <Sparkle16Regular className={styles.askPhotonIcon} /> Photon suggests
              </div>
              <div className={styles.proposalHeadline}>{s.headline}</div>
              <div className={styles.proposalReason}>{s.reason}</div>
              {s.kind === "rename" && !s.suggested_name && s.suggested_template && (
                <div style={{ fontFamily: "monospace", fontSize: "12px", marginTop: "4px", color: tokens.colorNeutralForeground2 }}>
                  {s.suggested_template}
                </div>
              )}
              {s.kind === "update_fields" && s.updates && (
                <ul style={{ margin: "4px 0 0 0", paddingLeft: "20px", fontSize: "12px" }}>
                  {s.updates.map((u) => (
                    <li key={u.field_id}>
                      {u.field_name}: {u.current === null ? "blank" : u.current} → <strong>{u.suggested}</strong>
                    </li>
                  ))}
                </ul>
              )}
              {showActions && !settled && (
                <div className={styles.proposalActions} style={{ marginTop: "6px" }}>
                  <Button
                    appearance="primary"
                    size="small"
                    disabled={busy}
                    onClick={() => actOnPmSuggestion(oppId, s, "approve")}
                  >
                    {busy ? <Spinner size="tiny" /> : "Approve"}
                  </Button>
                  <Button
                    appearance="secondary"
                    size="small"
                    disabled={busy}
                    onClick={() => actOnPmSuggestion(oppId, s, "dismiss")}
                  >
                    Dismiss
                  </Button>
                  {st?.error && (
                    <span className={styles.proposalError}>{st.error}</span>
                  )}
                </div>
              )}
              {showActions && settled && (
                <div className={styles.proposalSuccess}>
                  {st?.status === "approved"
                    ? s.kind === "rename"
                      ? `Renamed to "${s.suggested_name}". Logged Photon Set:.`
                      : `Updated ${(s.updates ?? []).map((u) => u.field_name).join(" and ")}. Logged Photon Set:.`
                    : "Dismissed — logged User Dismissed:."}
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const renderPmNotes = (opp: PmOpp) => {
    const oppId = opp.id || "";
    const all = opp.notes || [];
    const human = all.filter((n) => !n.is_photon);
    const photon = all.filter((n) => n.is_photon);
    const photonOpen = photonExpanded.has(oppId);
    const ns = noteState[oppId] || { open: false, text: "", busy: false };
    return (
      <div className={styles.notesBlock} style={{ marginTop: "8px" }}>
        {human.length === 0 ? (
          <div className={styles.noNotes}>No notes in the opportunity window.</div>
        ) : (
          human.map((n, i) => renderNote(n, `pmh-${oppId}-${i}`))
        )}
        {photon.length > 0 && (
          <div className={styles.photonBlock}>
            <button
              className={styles.photonToggle}
              onClick={() => togglePhotonExpanded(oppId)}
              aria-expanded={photonOpen}
            >
              {photonOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
              Photon activity ({photon.length})
            </button>
            {photonOpen && (
              <div style={{ marginTop: tokens.spacingVerticalS, display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
                {photon.map((n, i) => renderNote(n, `pmp-${oppId}-${i}`))}
              </div>
            )}
          </div>
        )}
        {!ns.open ? (
          <div className={styles.notesActions}>
            <button
              type="button"
              className={styles.addNoteToggle}
              onClick={() => setNoteField(oppId, { open: true })}
            >
              + Add a note
            </button>
          </div>
        ) : (
          <div className={styles.addNoteBlock}>
            <Textarea
              value={ns.text}
              onChange={(_, data) => setNoteField(oppId, { text: data.value })}
              placeholder="Write a note on this contact..."
              resize="vertical"
              rows={3}
              disabled={ns.busy}
            />
            <div className={styles.addNoteActions}>
              <Button
                appearance="primary"
                size="small"
                disabled={ns.busy || !ns.text.trim()}
                onClick={() => saveNote(oppId)}
              >
                {ns.busy ? <Spinner size="tiny" /> : "Save note"}
              </Button>
              <Button
                appearance="secondary"
                size="small"
                disabled={ns.busy}
                onClick={() => setNoteField(oppId, { open: false, text: "", error: undefined })}
              >
                Cancel
              </Button>
              {ns.error && <span className={styles.addNoteError}>{ns.error}</span>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderExpandToggle = (oppId: string, hasNotes: boolean) => {
    const open = expanded.has(oppId);
    return (
      <button
        className={styles.expandToggle}
        onClick={() => toggleExpanded(oppId)}
        aria-expanded={open}
      >
        {open ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
        {open ? "Hide notes & context" : hasNotes ? "Show notes & context" : "Show context"}
      </button>
    );
  };

  // Task-kind recs render completely differently from opp recs — no bucket
  // badge, no proposals/contradictions, no notes expansion. The two shapes
  // are peers in the ranked list but carry different verbs (Mark Complete /
  // Snooze vs. Approve/Dismiss proposals), so forking the whole card is
  // clearer than a single-function with dozens of conditionals.
  const renderTaskCard = (rec: Recommendation, key: string, isTopPriority: boolean = false) => {
    const taskId = rec.task_id || "";
    const contactId = rec.contact_id || "";
    const ts = taskState[taskId] || { status: "idle" };
    const busy = ts.status === "completing" || ts.status === "snoozing";
    const settled = ts.status === "completed" || ts.status === "snoozed";
    const dueLabel = formatDueLabel(rec.days_until_due, rec.is_overdue);
    const dueBadgeClass = rec.is_overdue
      ? styles.taskDueOverdue
      : rec.days_until_due === 0
        ? styles.taskDueToday
        : styles.taskDueSoon;
    const wrapperClass = isTopPriority
      ? `${styles.topPriorityCard} ${styles.taskCardLeftBorder}`
      : `${styles.recCard} ${styles.taskCardLeftBorder}`;
    const headlineClass = isTopPriority ? styles.topPriorityHeadline : styles.recHeadline;
    return (
      <div key={key} className={wrapperClass}>
        {isTopPriority && <div className={styles.topPriorityLabel}>Top priority</div>}
        <div className={styles.recHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={headlineClass}>
              <CheckmarkCircle16Regular style={{ verticalAlign: "middle", marginRight: 6, color: tokens.colorBrandForeground1 }} />
              {rec.headline}
              {renderConfidence(rec)}
            </div>
            <div className={styles.recSubline}>
              {rec.title ? rec.title : ""}
              {rec.title && rec.contact_name ? " · " : ""}
              {rec.contact_name || ""}
              {(rec.title || rec.contact_name) ? " · " : ""}
              <span className={`${styles.taskDueBadge} ${dueBadgeClass}`}>{dueLabel}</span>
            </div>
            {rec.confidence === "LOW" && rec.confidence_reason && (
              <span className={styles.confidenceReason}>{rec.confidence_reason}</span>
            )}
          </div>
          <Badge appearance="outline" color="brand">TASK</Badge>
        </div>
        {rec.reasoning && <div className={styles.reasoningText}>{rec.reasoning}</div>}
        {rec.body && <div className={styles.taskBody}>{rec.body}</div>}
        <div className={styles.taskActions}>
          {settled ? (
            <span className={styles.taskStatus}>
              {ts.status === "completed"
                ? "Done — marked complete and logged Photon Set:."
                : "Snoozed — pushed 3 days and logged User Snoozed:."}
            </span>
          ) : (
            <>
              <Button
                appearance="primary"
                size="small"
                icon={<CheckmarkCircle16Regular />}
                disabled={busy || !taskId}
                onClick={() => completeTask(taskId, contactId)}
              >
                {ts.status === "completing" ? <Spinner size="tiny" /> : "Mark complete"}
              </Button>
              <Button
                appearance="secondary"
                size="small"
                icon={<Clock16Regular />}
                disabled={busy || !taskId || !contactId}
                onClick={() => snoozeTask(taskId, contactId, 3)}
              >
                {ts.status === "snoozing" ? <Spinner size="tiny" /> : "Snooze 3d"}
              </Button>
              {ts.error && <span className={styles.taskError}>{ts.error}</span>}
            </>
          )}
          <button
            type="button"
            className={styles.emailLink}
            onClick={() => startTaskDraftEmail(taskId, contactId)}
            disabled={Boolean(emailState[taskId]) || !taskId || !contactId}
            title="Have Photon draft an email to the contact"
          >
            <Mail16Regular /> Draft email
          </button>
          <button
            type="button"
            className={styles.emailLink}
            onClick={() => startTaskTalkTrack(taskId, contactId)}
            disabled={Boolean(trackState[taskId]) || !taskId || !contactId}
            title="Generate a call prep doc for this task"
          >
            <Call16Regular /> Talk track
          </button>
          {onAskAboutOpp && rec.contact_name && (
            <button
              type="button"
              className={styles.askPhoton}
              onClick={() => onAskAboutOpp(buildTaskAskPrompt(rec), buildTaskAskTitle(rec))}
              title="Ask Photon to prep you for this task"
            >
              <Sparkle16Regular className={styles.askPhotonIcon} /> Ask Photon
            </button>
          )}
          {contactId && (
            <button
              type="button"
              className={styles.addNoteToggle}
              onClick={() => setNoteField(taskId, { open: true })}
              disabled={Boolean(noteState[taskId]?.open)}
              title="Add a note to this contact"
            >
              + Add a note
            </button>
          )}
          {contactId && (
            <a
              className={styles.openInGhl}
              href={`${GHL_BASE}/contacts/detail/${contactId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Open16Regular /> Open contact in CRM
            </a>
          )}
        </div>
        {renderTaskTalkTrack(taskId, contactId)}
        {renderTaskEmailDraft(taskId, contactId)}
        {(() => {
          const ns = noteState[taskId];
          if (!ns?.open) return null;
          return (
            <div className={styles.addNoteBlock}>
              <Textarea
                value={ns.text}
                onChange={(_, data) => setNoteField(taskId, { text: data.value })}
                placeholder="Write a note on this contact..."
                resize="vertical"
                rows={3}
                disabled={ns.busy}
              />
              <div className={styles.addNoteActions}>
                <Button
                  appearance="primary"
                  size="small"
                  disabled={ns.busy || !ns.text.trim()}
                  onClick={() => saveTaskNote(taskId, contactId)}
                >
                  {ns.busy ? <Spinner size="tiny" /> : "Save note"}
                </Button>
                <Button
                  appearance="secondary"
                  size="small"
                  disabled={ns.busy}
                  onClick={() => setNoteField(taskId, { open: false, text: "", error: undefined })}
                >
                  Cancel
                </Button>
                {ns.error && <span className={styles.addNoteError}>{ns.error}</span>}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // Event cards (Conference Coordinator v2). Read-only surfacing for now — the
  // interactive nudge/confirm/conversation actions arrive in a later increment.
  const eventPriorityBadge = (rec: Recommendation) => {
    const p = rec.priority || "medium";
    const color = p === "high" ? "danger" : p === "medium" ? "warning" : "informative";
    return <Badge appearance="tint" color={color}>{p.toUpperCase()}</Badge>;
  };

  const setEventCard = (id: string, patch: Record<string, unknown>) =>
    setEventCardState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  // Add a note to an event from its card — this is an interview turn, so
  // Photon reads it and the briefing refreshes (a gap may resolve, etc.).
  const addEventNote = async (cardId: string, eventId: string) => {
    const text = (eventCardState[cardId]?.noteText || "").trim();
    if (!text || !eventId) return;
    setEventCard(cardId, { noteBusy: true, err: undefined, noteMsg: undefined });
    try {
      const r = await crmApi.post<{ intake?: { reflect_back?: string; proposed_facts?: Array<{ field: string; value: string; display?: string; confirm_question?: string }> } }>(
        `/api/events/${encodeURIComponent(eventId)}/notes`, { body: text },
      );
      setEventCard(cardId, {
        noteBusy: false, noteText: "",
        noteMsg: r.intake?.reflect_back || "Noted.",
        proposedFacts: r.intake?.proposed_facts || [],
      });
      await loadAll();
    } catch (e) {
      setEventCard(cardId, { noteBusy: false, err: e instanceof Error ? e.message : String(e) });
    }
  };

  // Confirm a fact Photon proposed (commit it to the record) — e.g. tap the
  // "show dates" chip so the value lands and the gap card resolves.
  const confirmEventFact = async (cardId: string, eventId: string, field: string, value: string) => {
    setEventCard(cardId, { confirmingField: field, err: undefined });
    try {
      await crmApi.post(`/api/events/${encodeURIComponent(eventId)}/confirm`, { field, value });
      setEventCardState((s) => ({
        ...s,
        [cardId]: {
          ...s[cardId],
          confirmingField: undefined,
          proposedFacts: (s[cardId]?.proposedFacts || []).filter((f) => f.field !== field),
        },
      }));
      await loadAll();
    } catch (e) {
      setEventCard(cardId, { confirmingField: undefined, err: e instanceof Error ? e.message : String(e) });
    }
  };

  // Draft a nudge email to the teammate this card is chasing.
  const draftEventEmail = async (cardId: string, eventId: string, personId: string) => {
    setEventCard(cardId, { draftBusy: true, err: undefined, draft: null, sent: false });
    try {
      const d = await crmApi.post<{ draft_available?: boolean; reason?: string; subject?: string; body_plain?: string; body_html?: string; to_name?: string; to_email?: string }>(
        `/api/events/actions/nudge-draft`, { event_id: eventId, person_id: personId },
      );
      setEventCard(cardId, { draftBusy: false, draft: d });
    } catch (e) {
      setEventCard(cardId, { draftBusy: false, err: e instanceof Error ? e.message : String(e) });
    }
  };

  // Send the drafted nudge to the teammate via GHL (delivers to their email).
  const sendEventNudge = async (cardId: string, eventId: string, personId: string) => {
    const d = eventCardState[cardId]?.draft;
    if (!d?.subject || !(d.body_html || d.body_plain)) return;
    setEventCard(cardId, { sending: true, err: undefined });
    try {
      await crmApi.post(`/api/events/actions/nudge-send`, {
        event_id: eventId, person_id: personId,
        subject: d.subject,
        body_html: d.body_html || `<p>${(d.body_plain || "").replace(/\n/g, "<br>")}</p>`,
        body_plain: d.body_plain,
      });
      setEventCard(cardId, { sending: false, sent: true, draft: null });
      await loadAll();
    } catch (e) {
      setEventCard(cardId, { sending: false, err: e instanceof Error ? e.message : String(e) });
    }
  };

  const renderEventCard = (rec: Recommendation, key: string, isTopPriority: boolean = false) => {
    const wrapperClass = isTopPriority
      ? `${styles.topPriorityCard} ${styles.taskCardLeftBorder}`
      : `${styles.recCard} ${styles.taskCardLeftBorder}`;
    const headlineClass = isTopPriority ? styles.topPriorityHeadline : styles.recHeadline;
    const typeLabel = rec.card_type === "attendee" ? "Your task"
      : rec.card_type === "gap" ? "Needs info"
      : rec.card_type === "escalation" ? "Follow up"
      : "Show task";
    const cardId = rec.card_id || `${rec.event_id}:${rec.task_id || rec.card_type}`;
    const ui = eventCardState[cardId] || {};
    const notes = rec.event_notes || [];
    const canDraft = rec.card_type === "escalation" && !!rec.event_id && !!rec.person_id;
    return (
      <div key={key} className={wrapperClass}>
        {isTopPriority && <div className={styles.topPriorityLabel}>Top priority</div>}
        <div className={styles.recHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={headlineClass}>
              <CalendarLtr16Regular style={{ verticalAlign: "middle", marginRight: 6, color: tokens.colorBrandForeground1 }} />
              {rec.headline}
            </div>
            <div className={styles.recSubline}>
              {rec.event_name || "Event"}
              {" · "}{typeLabel}
              {rec.due_date ? ` · due ${rec.due_date}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {eventPriorityBadge(rec)}
            <Badge appearance="outline" color="brand">EVENT</Badge>
          </div>
        </div>
        {rec.reasoning && <div className={styles.reasoningText}>{rec.reasoning}</div>}

        <div className={styles.taskActions}>
          <button
            type="button"
            className={styles.addNoteToggle}
            onClick={() => setEventCard(cardId, { notesOpen: !ui.notesOpen })}
          >
            {ui.notesOpen ? "Hide notes & context" : "Show notes & context"}
          </button>
          {canDraft && (
            <button
              type="button"
              className={styles.emailLink}
              onClick={() => draftEventEmail(cardId, rec.event_id!, rec.person_id!)}
              disabled={ui.draftBusy}
              title={`Draft a reminder to ${rec.person_name || "this teammate"}`}
            >
              <Mail16Regular /> {ui.draftBusy ? "Drafting…" : "Draft email"}
            </button>
          )}
          {ui.err && <span className={styles.taskError}>{ui.err}</span>}
        </div>

        {ui.notesOpen && (
          <div className={styles.addNoteBlock}>
            {notes.length === 0 ? (
              <div className={styles.noNotes}>No notes yet.</div>
            ) : (
              notes.map((n, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div className={styles.recSubline}>{n.author || "—"}{n.ts ? ` · ${n.ts.slice(0, 10)}` : ""}</div>
                  <div>{n.body}</div>
                </div>
              ))
            )}
            <Textarea
              value={ui.noteText || ""}
              onChange={(_, data) => setEventCard(cardId, { noteText: data.value })}
              placeholder="Add a note — Photon reads it (dates, confirmations, a hold)…"
              resize="vertical"
              rows={2}
              disabled={ui.noteBusy}
            />
            <div className={styles.addNoteActions}>
              <Button
                appearance="primary"
                size="small"
                disabled={ui.noteBusy || !(ui.noteText || "").trim()}
                onClick={() => addEventNote(cardId, rec.event_id || "")}
              >
                {ui.noteBusy ? <Spinner size="tiny" /> : "Add note"}
              </Button>
              {ui.noteMsg && <span className={styles.taskStatus}>{ui.noteMsg}</span>}
            </div>
            {(() => {
              // Persistent chips from the card (survive reloads) merged with the
              // ephemeral ones from the last add-note, deduped by field+value.
              const chips = [...(rec.proposed_facts || []), ...(ui.proposedFacts || [])]
                .filter((f, i, arr) => arr.findIndex((x) => x.field === f.field && x.value === f.value) === i);
              if (chips.length === 0) return null;
              return (
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                <span className={styles.recSubline} style={{ width: "100%" }}>Tap to confirm:</span>
                {chips.map((f, i) => (
                  <Badge
                    key={i}
                    appearance="outline"
                    color="brand"
                    icon={ui.confirmingField === f.field ? <Spinner size="tiny" /> : <CheckmarkCircle16Regular />}
                    style={{ cursor: "pointer" }}
                    onClick={() => confirmEventFact(cardId, rec.event_id || "", f.field, f.value)}
                  >
                    {f.display || `${f.field}: ${f.value}`}
                  </Badge>
                ))}
              </div>
              );
            })()}
          </div>
        )}

        {ui.draft && (
          <div className={styles.addNoteBlock}>
            {ui.draft.draft_available === false ? (
              <span className={styles.taskError}>{ui.draft.reason || "Couldn’t draft this."}</span>
            ) : (
              <>
                <div className={styles.recSubline}>To: {ui.draft.to_name} &lt;{ui.draft.to_email}&gt;</div>
                <div style={{ fontWeight: 600, margin: "4px 0" }}>{ui.draft.subject}</div>
                <Textarea value={ui.draft.body_plain || ""} readOnly resize="vertical" rows={6} />
                <div className={styles.addNoteActions}>
                  <Button
                    appearance="primary"
                    size="small"
                    icon={ui.sending ? <Spinner size="tiny" /> : <Mail16Regular />}
                    disabled={ui.sending}
                    onClick={() => sendEventNudge(cardId, rec.event_id || "", rec.person_id || "")}
                  >
                    {ui.sending ? "Sending…" : `Send to ${rec.person_name || "teammate"}`}
                  </Button>
                  <Button size="small" onClick={() => void navigator.clipboard.writeText(ui.draft?.body_plain || "")}>Copy</Button>
                  <Button size="small" appearance="secondary" onClick={() => setEventCard(cardId, { draft: null })}>Dismiss</Button>
                </div>
              </>
            )}
          </div>
        )}
        {ui.sent && <div className={styles.taskStatus} style={{ marginTop: 6 }}>Nudge sent to {rec.person_name}. ✔</div>}
      </div>
    );
  };

  const renderRec = (rec: Recommendation, key: string) => {
    if (rec.kind === "task") return renderTaskCard(rec, key, false);
    if (rec.kind === "event") return renderEventCard(rec, key, false);
    const oppId = rec.opp_id || "";
    const bucket = rec.bucket || "owned";
    const open = expanded.has(oppId);
    // Pipeline-wide profiles (e.g. existing_customer) don't have ownership
    // buckets — hide the bucket badge entirely. LERT path keeps the existing
    // "default to owned when missing" fallback for backward compat with
    // briefings generated before bucket was a required field.
    // Bucket badges are LERT-only — pipeline-wide and PM-by-state profiles
    // don't have owned/territory partitions.
    const showBucketBadge =
      me?.profile?.bucket_strategy !== "pipeline_wide" &&
      me?.profile?.bucket_strategy !== "pm_by_state" &&
      me?.profile?.bucket_strategy !== "lert_rollup";
    return (
      <div key={key} className={styles.recCard}>
        <div className={styles.recHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={styles.recHeadline}>
              {rec.headline}
              {renderConfidence(rec)}
            </div>
            <div className={styles.recSubline}>
              {rec.opp_name}
              {rec.contact ? ` · ${rec.contact}` : ""}
              {rec.pipeline ? ` · ${rec.pipeline}` : ""}
              {rec.stage ? ` · ${rec.stage}` : ""}
            </div>
            {rec.confidence === "LOW" && rec.confidence_reason && (
              <span className={styles.confidenceReason}>{rec.confidence_reason}</span>
            )}
          </div>
          {showBucketBadge && (
            <Badge appearance="outline" color={BUCKET_COLOR[bucket]}>
              {BUCKET_LABEL[bucket]}
            </Badge>
          )}
          {/* Owner tag — sales_admin_lert rollup only. Whose deal this is, so
              Allison can route the nudge and read the briefing per-rep. */}
          {rec.owner_name && (
            <Badge
              appearance="filled"
              color={rec.owner_name === "Unassigned" ? "warning" : "informative"}
              title="Owning LERT rep"
            >
              {rec.owner_name}
            </Badge>
          )}
        </div>
        {renderContradictions(rec.contradictions)}
        {rec.reasoning && <div className={styles.reasoningText}>{rec.reasoning}</div>}
        {rec.decision_tree && rec.decision_tree.length > 0 && (
          <div style={{
            marginTop: tokens.spacingVerticalS,
            padding: tokens.spacingVerticalS,
            background: tokens.colorNeutralBackground2,
            borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
            fontSize: "13px",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Check before acting</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {rec.decision_tree.map((step, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  <span>{step.check}</span>
                  {step.if_yes && (
                    <div style={{ marginLeft: 0, color: tokens.colorNeutralForeground2 }}>
                      → If yes: {step.if_yes}
                    </div>
                  )}
                  {step.if_no && (
                    <div style={{ marginLeft: 0, color: tokens.colorNeutralForeground2 }}>
                      → If no: {step.if_no}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {renderProposals(rec.proposals)}
        {renderPmSuggests(oppId, rec.photon_suggests)}
        {renderExpandToggle(oppId, (rec.notes?.length ?? 0) > 0)}
        {open && renderNotes(rec)}
      </div>
    );
  };

  // Team lead (Greg, player-manager): the panel exposes a [Team Cockpit |
  // My Day] toggle. `teamView` picks which surface renders.
  const isTeamLead = me?.profile?.bucket_strategy === "team_lert_aggregate";

  return (
    <div className={styles.container}>
      {/* Redundant "Tackle My Day" header/logo removed — the active tab already
          names this surface (same reasoning as the CRM Assistant logo). */}

      {/* Leaderboard moved to the top-of-CRM "Daily News" ticker (TmdLeaderboard
          mounted in CRM.tsx) so it shows on every tab, not just here. */}

      {(me?.is_lert || me?.profile || me?.can_impersonate_lert) && (
        <div className={styles.controls}>
          {me?.can_impersonate_lert && (
            <Field label="View as ..." style={{ maxWidth: "360px" }}>
              <select
                className={styles.nativeSelect}
                value={impersonate}
                onChange={(e) => setImpersonate(e.target.value)}
              >
                <option value="">— My identity —</option>
                {pickerMembers.map((m) => (
                  <option
                    key={`${m.mechanism}:${m.value}`}
                    value={`${m.mechanism}:${m.value}`}
                  >
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Button onClick={loadAll} disabled={loading}>
            {loading ? <Spinner size="tiny" /> : "Refresh"}
          </Button>
        </div>
      )}

      {me?.is_impersonating && (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>
              Viewing as: {me.target_name || me.lert_name}
            </MessageBarTitle>
            All briefing content and action targets reflect this user. Audit
            log entries record {me.actor_name} as the actor.
          </MessageBarBody>
        </MessageBar>
      )}

      {me && !me.profile && !me.is_lert ? (
        <div className={styles.emptyState}>
          <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: tokens.spacingVerticalM }}>
            Nothing to tackle here yet.
          </div>
          <Text>
            Tackle My Day isn&apos;t configured for your role yet. If there&apos;s
            something you&apos;d find useful here, tell Mark — he&apos;s mapping out
            the next version.
          </Text>
        </div>
      ) : loading ? (
        <TmdBriefingProgress />
      ) : (
        <>
          {isTeamLead && (
            <div
              className={styles.controls}
              style={{ marginBottom: tokens.spacingVerticalS }}
            >
              <Button
                size="small"
                appearance={teamView === "cockpit" ? "primary" : "subtle"}
                onClick={() => setTeamView("cockpit")}
              >
                Team Cockpit
              </Button>
              <Button
                size="small"
                appearance={teamView === "myday" ? "primary" : "subtle"}
                onClick={() => setTeamView("myday")}
              >
                My Day
              </Button>
            </div>
          )}
          {isTeamLead && teamView === "cockpit" ? (
            <TeamCockpitPanel
              data={teamCockpit}
              loading={loading}
              onOpenOpp={openOpp}
              briefing={teamBriefing}
              briefingLoading={teamBriefingLoading}
              onCoachNote={coachNote}
              onSuggestCoachNote={suggestCoachNote}
              drill={drill}
              onOpenDrill={openDrill}
              onCloseDrill={closeDrill}
              onFetchCheckinContacts={fetchCheckinContacts}
            />
          ) : (
          <>
          {briefing?.counts && (
            <div className={styles.metaRow}>
              {me?.profile?.bucket_strategy === "pm_by_state" ? (
                // PM profile (Sean / Carson). State routing is for triage,
                // not buckets — show the PM, their slice of the pipeline,
                // and the pipeline total.
                <>
                  <span>{briefing.pm_display_name || me.profile.display_name}</span>
                  <span>·</span>
                  <span>
                    {briefing.total ?? briefing.counts.opps ?? 0} of {briefing.pipeline_total ?? 0} ops opps
                  </span>
                </>
              ) : me?.profile?.bucket_strategy === "pipeline_wide" ? (
                // Non-LERT profile (e.g. existing_customer / Tatiana). No
                // ownership buckets; show profile + pipeline + opp count.
                <>
                  <span>{me.profile.display_name}</span>
                  <span>·</span>
                  <span>{briefing.counts.opps ?? 0} opps in pipeline</span>
                </>
              ) : me?.profile?.bucket_strategy === "lert_rollup" ? (
                // Sales Admin — LERT (Allison). Cross-team rollup: show the
                // in-scope opp count, reps represented, and unassigned tally.
                <>
                  <span>{me.profile.display_name}</span>
                  <span>·</span>
                  <span>
                    {briefing.counts.opps ?? 0} in-scope · {briefing.counts.reps ?? 0} reps
                    {(briefing.counts.unassigned ?? 0) > 0
                      ? ` · ${briefing.counts.unassigned} unassigned`
                      : ""}
                  </span>
                </>
              ) : (
                // LERT (default). Existing meta row.
                <>
                  <span>
                    {me?.lert_name} · territories: {(me?.territories ?? []).join(", ") || "—"}
                  </span>
                  <span>·</span>
                  <span>
                    {briefing.counts.owned ?? 0} owned · {briefing.counts.territory_unassigned ?? 0} unassigned · {briefing.counts.territory_other_owned ?? 0} other-LERT
                  </span>
                </>
              )}
              {briefing.generated_at && (
                <>
                  <span>·</span>
                  <span>{new Date(briefing.generated_at).toLocaleString()}</span>
                </>
              )}
            </div>
          )}

          {briefing?.profile_id === "event_coordinator" && (
            <div style={{ marginBottom: tokens.spacingVerticalM }}>
              <Button
                appearance="primary"
                icon={<CalendarLtr16Regular />}
                onClick={() => setConvoOpen(true)}
              >
                Set up or manage a show
              </Button>
            </div>
          )}

          <Dialog open={convoOpen} onOpenChange={(_, d) => { if (!d.open) { setConvoOpen(false); void loadAll(); } }}>
            <DialogSurface style={{ maxWidth: "860px", width: "90vw" }}>
              <DialogBody>
                <DialogTitle>Conferences & Shows</DialogTitle>
                <DialogContent>
                  <EventConversation />
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" onClick={() => { setConvoOpen(false); void loadAll(); }}>Done</Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>

          {briefing?.top_priority ? (
            briefing.top_priority.kind === "task" ? (
              renderTaskCard(briefing.top_priority, "top", true)
            ) : briefing.top_priority.kind === "event" ? (
              renderEventCard(briefing.top_priority, "top", true)
            ) : (
              <div className={styles.topPriorityCard}>
                <div className={styles.topPriorityLabel}>Top priority</div>
                <div className={styles.topPriorityHeadline}>
                  {briefing.top_priority.headline}
                  {renderConfidence(briefing.top_priority)}
                </div>
                <div className={styles.recSubline}>
                  {briefing.top_priority.opp_name}
                  {briefing.top_priority.contact ? ` · ${briefing.top_priority.contact}` : ""}
                  {briefing.top_priority.pipeline ? ` · ${briefing.top_priority.pipeline}` : ""}
                  {briefing.top_priority.stage ? ` · ${briefing.top_priority.stage}` : ""}
                </div>
                {briefing.top_priority.confidence === "LOW" && briefing.top_priority.confidence_reason && (
                  <span className={styles.confidenceReason}>{briefing.top_priority.confidence_reason}</span>
                )}
                {renderContradictions(briefing.top_priority.contradictions)}
                {briefing.top_priority.reasoning && (
                  <div className={styles.reasoningText}>{briefing.top_priority.reasoning}</div>
                )}
                {renderProposals(briefing.top_priority.proposals)}
                {renderPmSuggests(briefing.top_priority.opp_id || "", briefing.top_priority.photon_suggests)}
                {renderExpandToggle(briefing.top_priority.opp_id || "", (briefing.top_priority.notes?.length ?? 0) > 0)}
                {expanded.has(briefing.top_priority.opp_id || "") &&
                  renderNotes(briefing.top_priority)}
              </div>
            )
          ) : (
            <div className={styles.recCard}>
              {briefing?.message ? (
                <Text>{briefing.message}</Text>
              ) : (
                <Text className={styles.notes}>No top priority today.</Text>
              )}
            </div>
          )}

          {renderConfidenceChecks(briefing?.confidence_checks)}

          {briefing?.recommendations && briefing.recommendations.length > 0 && (
            <>
              <Text weight="semibold" size={400}>Next up</Text>
              {briefing.recommendations.map((rec, i) =>
                renderRec(rec, `${rec.task_id || rec.opp_id || "rec"}-${i}`),
              )}
            </>
          )}

          {extraRecs.length > 0 && (
            <>
              <Text weight="semibold" size={400}>More from your pipeline</Text>
              {extraRecs.map((rec, i) =>
                renderRec(rec, `extra-${rec.task_id || rec.opp_id || "rec"}-${i}`),
              )}
            </>
          )}

          {/* Show More is available for every briefing path — backend
              filters opps (and tasks for LERT) before Gemini ranks. Hidden
              only when the last response came back empty (noMoreRecs) or
              when there's nothing on screen yet to exclude. */}
          {briefing && !noMoreRecs &&
           (briefing.top_priority || (briefing.recommendations?.length ?? 0) > 0) && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                appearance="primary"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? <Spinner size="tiny" /> : "Show more"}
              </Button>
            </div>
          )}
          {noMoreRecs && (
            <Text className={styles.notes}>
              You&apos;ve seen everything Photon has for this pipeline today.
            </Text>
          )}

          {briefing?.notes && (
            <Text className={styles.notes}>{briefing.notes}</Text>
          )}
          </>
          )}
        </>
      )}
    </div>
  );
};

export default TackleMyDayPanel;
